import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import seatMapService from '../../services/seatMapService';
import adminSeatMapService from '../../services/adminSeatMapService';
import organizerService from '../../services/organizerService';

// ── Constants ──────────────────────────────────────────────────────────────────
const STORAGE_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/api$/, '');
const storageUrl   = path => (!path ? null : path.startsWith('http') ? path : `${STORAGE_BASE}${path}`);

const PALETTE = ['#3B82F6','#EF4444','#10B981','#F59E0B','#8B5CF6','#EC4899','#06B6D4','#84CC16','#F97316','#64748B'];
const GRID_PX   = 20;
const SNAP_PX   = 20;
const MAX_HIST  = 60;
const HANDLE_R  = 5;
const ROT_DIST  = 26;

let _uid = 0;
const tmpId   = () => `_t${++_uid}`;
const isTmp   = id => String(id).startsWith('_t');
const d2      = (a, b) => Math.sqrt((a.x-b.x)**2 + (a.y-b.y)**2);
const clamp   = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const r2d     = r => r * 180 / Math.PI;
const snapV   = (v, on) => on ? Math.round(v / SNAP_PX) * SNAP_PX : v;

function toWorld(e, svgEl, zoom, pan) {
  const r = svgEl.getBoundingClientRect();
  return { x: (e.clientX - r.left - pan.x) / zoom, y: (e.clientY - r.top - pan.y) / zoom };
}

function seatsInRect(rb, seats, sec) {
  const minX = Math.min(rb.x0, rb.x1), maxX = Math.max(rb.x0, rb.x1);
  const minY = Math.min(rb.y0, rb.y1), maxY = Math.max(rb.y0, rb.y1);
  const rot = sec.rotation || 0;
  const cx  = sec.position_x + sec.width / 2;
  const cy  = sec.position_y + sec.height / 2;
  const rad = rot * Math.PI / 180;
  const cos = Math.cos(rad), sin = Math.sin(rad);
  const ids = new Set();
  for (const s of seats) {
    let wx = sec.position_x + s.x;
    let wy = sec.position_y + s.y;
    if (rot !== 0) {
      const dx = wx - cx, dy = wy - cy;
      wx = cos * dx - sin * dy + cx;
      wy = sin * dx + cos * dy + cy;
    }
    if (wx >= minX && wx <= maxX && wy >= minY && wy <= maxY)
      ids.add(s.id);
  }
  return ids;
}

// Returns true if local position (lx, ly) inside this section's shape
// Test if a NORMALIZED position (nx,ny ∈ [0,1]) falls inside the section shape.
// Maps back to the section's original local coordinate space for the PIP/ellipse check.
function positionFitsInShape(sec, nx, ny) {
  const { position_x: spx, position_y: spy, width: w, height: h } = sec;
  const lx = nx * w, ly = ny * h;
  if (sec.shape_type === 'circle') {
    const rx = w / 2, ry = h / 2;
    const dx = (lx - rx) / rx, dy = (ly - ry) / ry;
    return dx * dx + dy * dy <= 0.84 * 0.84;
  }
  if (sec.shape_type === 'polygon' && sec.shape_data?.points?.length) {
    return pointInPolygon(spx + lx, spy + ly, sec.shape_data.points);
  }
  return true; // rect — always valid
}

// Infer the editor workspace dimensions from stored value or seat extents.
function inferEditorDims(sec) {
  if (sec._editorW) return { editorW: sec._editorW, editorH: sec._editorH };
  if (sec.shape_data?._editorW) return { editorW: sec.shape_data._editorW, editorH: sec.shape_data._editorH };
  const seats = sec.seats || [];
  if (!seats.length) return { editorW: sec.width, editorH: sec.height };
  const maxX = Math.max(...seats.map(s => s.x));
  const maxY = Math.max(...seats.map(s => s.y));
  return {
    editorW: Math.max(sec.width, maxX + 24),
    editorH: Math.max(sec.height, maxY + 24),
  };
}

const DENSITY_SPACING = { compact: 14, normal: 20, spacious: 28 };

// ─── SEAT GEOMETRY ENGINE ─────────────────────────────────────────────────────
// All functions below work in the section's PRE-SVG-rotation world coordinate
// space. sec.rotation is handled by the SVG transform, so we only need the
// raw polygon/ellipse/rect geometry. The output seat.x / seat.y values are
// LOCAL coordinates (world - section_origin), consistent with how the renderer
// positions seats: wx = sec.position_x + seat.x.

// Return the boundary polygon of a section in pre-SVG-rotation world coords.
function getSectionBasePoly(sec) {
  const { position_x: px, position_y: py, width: w, height: h } = sec;
  if (sec.shape_type === 'polygon' && sec.shape_data?.points?.length >= 3) {
    return sec.shape_data.points;
  }
  if (sec.shape_type === 'circle') {
    const cx = px + w / 2, cy = py + h / 2, rx = w / 2, ry = h / 2;
    const N = 64;
    return Array.from({ length: N }, (_, i) => ({
      x: cx + rx * Math.cos(2 * Math.PI * i / N),
      y: cy + ry * Math.sin(2 * Math.PI * i / N),
    }));
  }
  return [
    { x: px,     y: py     },
    { x: px + w, y: py     },
    { x: px + w, y: py + h },
    { x: px,     y: py + h },
  ];
}

// PCA: compute the angle (radians) of the polygon's principal axis — the
// direction along which seats in a single row will be laid out.
function computeRowAngle(poly) {
  const n = poly.length;
  let cx = 0, cy = 0;
  for (const p of poly) { cx += p.x; cy += p.y; }
  cx /= n; cy /= n;
  let sxx = 0, sxy = 0, syy = 0;
  for (const p of poly) {
    const dx = p.x - cx, dy = p.y - cy;
    sxx += dx * dx; sxy += dx * dy; syy += dy * dy;
  }
  return 0.5 * Math.atan2(2 * sxy, sxx - syy);
}

// Compute all u-axis intersections of the scan line at depth vRow with
// the polygon (expressed in the (u, v) local frame). Returns sorted u-values.
function rowLineIntersections(localPoly, vRow) {
  const n = localPoly.length;
  const us = [];
  for (let i = 0; i < n; i++) {
    const a = localPoly[i], b = localPoly[(i + 1) % n];
    if ((a.v <= vRow && b.v > vRow) || (b.v <= vRow && a.v > vRow)) {
      const t = (vRow - a.v) / (b.v - a.v);
      us.push(a.u + t * (b.u - a.u));
    }
  }
  return us.sort((a, b) => a - b);
}

// Unified seat generation. Both auto-fill and row-config call this.
//
// mode 'auto':  modeParams = { totalSeats, startSeat }
//               Binary-searches for the minimum uniform scale factor that fits
//               totalSeats at the chosen spacing.  The section stays the same
//               size on the main map; only the detail-view workspace expands.
//
// mode 'rows':  modeParams = { rowCounts: number[] }
//               Expands the workspace to give each row enough width/height for
//               the requested seat count, then distributes seats by clipping
//               each row against the (scaled) section polygon.
//
// rowAngleDeg:  null = auto (PCA), number = manual override in degrees.
function generateSeatsForSection(sec, mode, modeParams, spacing, rowAngleDeg = null) {
  const rowAlpha = i => {
    const A = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    return i < 26 ? A[i] : A[Math.floor(i / 26) - 1] + A[i % 26];
  };
  const { position_x: px, position_y: py, width: w, height: h } = sec;

  // 1. Pre-rotation polygon (world coords, ignoring sec.rotation which SVG handles)
  const worldPoly = getSectionBasePoly(sec);

  // 2. Row-direction angle
  const angleRad = rowAngleDeg != null
    ? rowAngleDeg * Math.PI / 180
    : computeRowAngle(worldPoly);
  const uHat = { x: Math.cos(angleRad), y: Math.sin(angleRad) }; // along rows
  const vHat = { x: -uHat.y, y: uHat.x };                        // row-to-row depth

  // Build the polygon scaled to fill an editor of size (eW × eH), then project
  // into the (u, v) PCA frame centred on (eW/2, eH/2).
  // This keeps seat coords in [0, eW] × [0, eH] — always non-negative.
  function buildLocalPoly(eW, eH) {
    const scaleX = eW / w, scaleY = eH / h;
    const cx = eW / 2, cy = eH / 2;
    let pts;
    if (sec.shape_type === 'polygon' && sec.shape_data?.points?.length >= 3) {
      pts = sec.shape_data.points.map(p => ({
        x: (p.x - px) * scaleX,
        y: (p.y - py) * scaleY,
      }));
    } else if (sec.shape_type === 'circle') {
      const N = 64;
      pts = Array.from({ length: N }, (_, i) => ({
        x: cx + (eW / 2) * Math.cos(2 * Math.PI * i / N),
        y: cy + (eH / 2) * Math.sin(2 * Math.PI * i / N),
      }));
    } else {
      pts = [{ x: 0, y: 0 }, { x: eW, y: 0 }, { x: eW, y: eH }, { x: 0, y: eH }];
    }
    return pts.map(p => ({
      u: (p.x - cx) * uHat.x + (p.y - cy) * uHat.y,
      v: (p.x - cx) * vHat.x + (p.y - cy) * vHat.y,
    }));
  }

  // Count seats that fit inside the polygon at a given editor size + spacing
  function countAt(eW, eH, sp) {
    const lp = buildLocalPoly(eW, eH);
    const vMin = Math.min(...lp.map(p => p.v));
    const vMax = Math.max(...lp.map(p => p.v));
    let cnt = 0;
    for (let vRow = vMin + sp / 2; vRow < vMax - sp / 4; vRow += sp) {
      const us = rowLineIntersections(lp, vRow);
      for (let i = 0; i + 1 < us.length; i += 2)
        cnt += Math.max(0, Math.floor((us[i + 1] - us[i] + sp * 0.01) / sp));
    }
    return cnt;
  }

  // Generate all seat positions for a specific editor size
  function generateAt(eW, eH, sp, gMode, gParams) {
    const lp = buildLocalPoly(eW, eH);
    const vMin = Math.min(...lp.map(p => p.v));
    const vMax = Math.max(...lp.map(p => p.v));
    const cx = eW / 2, cy = eH / 2;

    const toSeat = (u, v) => ({
      x: Math.round((cx + u * uHat.x + v * vHat.x) * 10) / 10,
      y: Math.round((cy + u * uHat.y + v * vHat.y) * 10) / 10,
    });

    const out = [];

    if (gMode === 'auto') {
      const { totalSeats, startSeat } = gParams;
      let rowIdx = 0, seatNum = startSeat, placed = 0;
      outer:
      for (let vRow = vMin + sp / 2; vRow < vMax - sp / 4; vRow += sp) {
        const us = rowLineIntersections(lp, vRow);
        if (us.length < 2) { rowIdx++; continue; }
        const rowLabel = rowAlpha(rowIdx);
        for (let i = 0; i + 1 < us.length; i += 2) {
          const segLen = us[i + 1] - us[i];
          const n = Math.max(0, Math.floor((segLen + sp * 0.01) / sp));
          if (!n) continue;
          const offset = (segLen - (n - 1) * sp) / 2; // centre seats within segment
          for (let j = 0; j < n && placed < totalSeats; j++) {
            out.push({ row: rowLabel, seat_number: String(seatNum), label: rowLabel + seatNum,
              ...toSeat(us[i] + offset + j * sp, vRow) });
            seatNum++; placed++;
          }
          if (placed >= totalSeats) break outer;
        }
        rowIdx++;
      }
    } else {
      const { rowCounts } = gParams;
      const numRows = rowCounts.length;
      // Rows spread evenly across the full polygon depth (same as auto-fill
      // scanline philosophy). Fixed sp spacing between seats — same spacing and
      // seat size in every row, matching auto-fill behaviour.
      const vSpan = vMax - vMin;
      const rowStep = numRows <= 1 ? vSpan : vSpan / numRows;
      const vStart  = vMin + rowStep / 2;
      rowCounts.forEach((count, r) => {
        if (count <= 0) return;
        const vRow = vStart + r * rowStep;
        const us   = rowLineIntersections(lp, vRow);
        if (us.length < 2) return;
        const rowLabel = rowAlpha(r);
        let placed = 0;
        // Fixed sp spacing, centred in each polygon segment.
        // The binary search below guarantees segLen ≥ count×sp so every seat fits.
        for (let i = 0; i + 1 < us.length && placed < count; i += 2) {
          const segLen = us[i + 1] - us[i];
          const n = Math.min(count - placed, Math.max(0, Math.floor((segLen + sp * 0.01) / sp)));
          if (!n) continue;
          const offset = (segLen - (n - 1) * sp) / 2;
          for (let j = 0; j < n; j++) {
            out.push({ row: rowLabel, seat_number: String(placed + 1), label: rowLabel + (placed + 1),
              ...toSeat(us[i] + offset + j * sp, vRow) });
            placed++;
          }
        }
      });
    }
    return out;
  }

  let positions, editorW, editorH;

  if (mode === 'auto') {
    const { totalSeats, startSeat } = modeParams;

    // Binary-search for the minimum uniform scale where totalSeats fit.
    // Restores the original "second-layer expansion" behaviour: the main-map
    // section is unchanged; only the detail-view workspace grows.
    let scale = 1;
    if (countAt(w, h, spacing) < totalSeats) {
      let lo = 1, hi = 200;
      for (let iter = 0; iter < 60; iter++) {
        const mid = (lo + hi) / 2;
        if (countAt(Math.ceil(w * mid), Math.ceil(h * mid), spacing) >= totalSeats) hi = mid;
        else lo = mid;
        if (hi - lo < 0.002) break;
      }
      scale = hi;
    }
    editorW = Math.ceil(w * scale);
    editorH = Math.ceil(h * scale);
    positions = generateAt(editorW, editorH, spacing, 'auto', { totalSeats, startSeat });

  } else {
    const { rowCounts } = modeParams;
    const numRows = rowCounts.length;

    // Check whether all rows fit with enough width for their seats at the
    // chosen spacing. Uses the same uniform scale as auto-fill (w*s, h*s) so
    // the polygon's aspect ratio is preserved in the detail view.
    const allRowsFit = (eW, eH) => {
      const lp = buildLocalPoly(eW, eH);
      const vn = Math.min(...lp.map(p => p.v));
      const vx = Math.max(...lp.map(p => p.v));
      // Mirror the adaptive row positions used in generateAt
      const rowStep = numRows <= 1 ? (vx - vn) : (vx - vn) / numRows;
      const vs = vn + rowStep / 2;
      for (let r = 0; r < rowCounts.length; r++) {
        if (rowCounts[r] <= 0) continue;
        const vRow = vs + r * rowStep;
        const us = rowLineIntersections(lp, vRow);
        if (us.length < 2) return false;
        let len = 0;
        for (let i = 0; i + 1 < us.length; i += 2) len += us[i + 1] - us[i];
        // Require every row to have enough width for its seats at the chosen
        // spacing — identical criterion to auto-fill's countAt check.
        const minLen = rowCounts[r] <= 1 ? spacing : rowCounts[r] * spacing;
        if (len < minLen) return false;
      }
      return true;
    };

    // Start from the original section size; binary-search the minimum uniform
    // scale (same approach as auto-fill) that makes every row fit.
    let scale = 1;
    if (!allRowsFit(w, h)) {
      let lo = 1, hi = 300;
      for (let iter = 0; iter < 60; iter++) {
        const mid = (lo + hi) / 2;
        if (allRowsFit(Math.ceil(w * mid), Math.ceil(h * mid))) hi = mid;
        else lo = mid;
        if (hi - lo < 0.002) break;
      }
      scale = hi;
    }

    editorW = Math.ceil(w * scale);
    editorH = Math.ceil(h * scale);
    positions = generateAt(editorW, editorH, spacing, 'rows', { rowCounts });
  }

  // Compute the minimum actual center-to-center distance between consecutive
  // seats in any row. This is the effective spacing used for rendering seat size.
  let effectiveSpacing = spacing;
  if (positions.length > 1) {
    const byRow = {};
    for (const s of positions) {
      if (!byRow[s.row]) byRow[s.row] = [];
      byRow[s.row].push(s);
    }
    let minD = Infinity;
    for (const seats of Object.values(byRow)) {
      const sorted = [...seats].sort((a, b) => a.x - b.x || a.y - b.y);
      for (let i = 1; i < sorted.length; i++) {
        const dx = sorted[i].x - sorted[i - 1].x;
        const dy = sorted[i].y - sorted[i - 1].y;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d > 0.1) minD = Math.min(minD, d);
      }
    }
    if (minD < Infinity) effectiveSpacing = minD;
  }

  return { positions, editorW, editorH, effectiveSpacing };
}

function pointInPolygon(px, py, polygon) {
  let inside = false;
  const n = polygon.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = polygon[i].x, yi = polygon[i].y;
    const xj = polygon[j].x, yj = polygon[j].y;
    if (((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi))
      inside = !inside;
  }
  return inside;
}

// ── SVG → Sections parser ──────────────────────────────────────────────────────
function parseSVGIntoSections(svgText, canvasW, canvasH) {
  const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
  if (doc.querySelector('parsererror')) throw new Error('Invalid SVG file');
  const svgEl = doc.querySelector('svg');
  if (!svgEl) throw new Error('No <svg> element found');

  // Parse viewBox → source coordinate space
  let vbX = 0, vbY = 0, vbW, vbH;
  const vb = svgEl.getAttribute('viewBox');
  if (vb) {
    const p = vb.trim().split(/[\s,]+/).map(Number);
    if (p.length >= 4) [vbX, vbY, vbW, vbH] = p;
  }
  if (!vbW) vbW = parseFloat(svgEl.getAttribute('width')  || '1200') || 1200;
  if (!vbH) vbH = parseFloat(svgEl.getAttribute('height') || '800')  || 800;

  // Uniform scale + offset to centre SVG inside the canvas
  const PAD   = 40;
  const scale = Math.min((canvasW - PAD * 2) / vbW, (canvasH - PAD * 2) / vbH);
  const offX  = PAD + (canvasW - PAD * 2 - vbW * scale) / 2 - vbX * scale;
  const offY  = PAD + (canvasH - PAD * 2 - vbH * scale) / 2 - vbY * scale;

  const cx = v => v * scale + offX;
  const cy = v => v * scale + offY;

  // 2-D affine matrix helpers  [a, b, c, d, e, f]
  //   x' = a*x + c*y + e
  //   y' = b*x + d*y + f
  const ID = [1, 0, 0, 1, 0, 0];
  function mul(A, B) {
    const [a1,b1,c1,d1,e1,f1] = A, [a2,b2,c2,d2,e2,f2] = B;
    return [
      a1*a2+c1*b2, b1*a2+d1*b2,
      a1*c2+c1*d2, b1*c2+d1*d2,
      a1*e2+c1*f2+e1, b1*e2+d1*f2+f1,
    ];
  }
  function applyM(m, x, y) {
    const [a,b,c,d,e,f] = m;
    return { x: a*x+c*y+e, y: b*x+d*y+f };
  }
  function parseTx(str) {
    if (!str) return ID;
    let r = ID;
    const re = /([a-zA-Z]+)\s*\(([^)]*)\)/g;
    let m;
    while ((m = re.exec(str)) !== null) {
      const fn = m[1].toLowerCase();
      const v  = m[2].trim().split(/[\s,]+/).map(Number);
      let M = ID;
      if (fn === 'translate') {
        M = [1,0,0,1,v[0]||0,v[1]||0];
      } else if (fn === 'scale') {
        M = [v[0]||1,0,0,v.length>1?v[1]:v[0]||1,0,0];
      } else if (fn === 'rotate') {
        const θ = (v[0]||0)*Math.PI/180, cs = Math.cos(θ), sn = Math.sin(θ);
        M = v.length >= 3
          ? mul([1,0,0,1,v[1],v[2]], mul([cs,sn,-sn,cs,0,0],[1,0,0,1,-v[1],-v[2]]))
          : [cs,sn,-sn,cs,0,0];
      } else if (fn === 'matrix') {
        M = [v[0],v[1],v[2],v[3],v[4],v[5]];
      }
      r = mul(r, M);
    }
    return r;
  }

  function bbox(pts) {
    const xs = pts.map(p=>p.x), ys = pts.map(p=>p.y);
    return { minX:Math.min(...xs), minY:Math.min(...ys), maxX:Math.max(...xs), maxY:Math.max(...ys) };
  }
  function isIdentityScale(m) {
    return Math.abs(m[0]-1)<0.01 && Math.abs(m[3]-1)<0.01 && Math.abs(m[1])<0.01 && Math.abs(m[2])<0.01;
  }

  // Sample a path element via the live DOM (getTotalLength / getPointAtLength)
  function samplePath(d) {
    const NS = 'http://www.w3.org/2000/svg';
    const tmp = document.createElementNS(NS, 'svg');
    tmp.style.cssText = 'position:absolute;left:-9999px;top:-9999px;width:0;height:0;overflow:hidden;';
    const p = document.createElementNS(NS, 'path');
    p.setAttribute('d', d);
    tmp.appendChild(p);
    document.body.appendChild(tmp);
    const len = p.getTotalLength();
    const pts = [];
    if (len > 0) {
      const n = Math.max(8, Math.min(80, Math.round(len / 6)));
      for (let i = 0; i <= n; i++) {
        const s = p.getPointAtLength((i / n) * len);
        pts.push({ x: s.x, y: s.y });
      }
    }
    document.body.removeChild(tmp);
    return pts;
  }

  function getFill(el, inherited) {
    const attr  = el.getAttribute('fill');
    const style = (el.getAttribute('style') || '').match(/fill\s*:\s*([^;]+)/)?.[1]?.trim();
    const f = attr || style;
    if (!f || f === 'none' || f === 'transparent' || f.startsWith('url(')) return inherited;
    return f;
  }

  const SKIP = new Set(['defs','style','script','title','desc','metadata','symbol',
    'use','text','tspan','marker','pattern','mask','clippath',
    'lineargradient','radialgradient','filter']);

  const MIN_PX = 20; // minimum canvas-space size to keep
  const sections = [];
  let idx = 1;

  function walk(el, mx, fill, parentId) {
    const tag = (el.tagName||'').toLowerCase().replace(/^svg:/,'');
    if (!tag || SKIP.has(tag)) return;

    const localMx = parseTx(el.getAttribute('transform'));
    const m       = mul(mx, localMx);
    const color   = getFill(el, fill);
    const titleEl = el.querySelector && el.querySelector(':scope > title');
    const ownId   = el.getAttribute('id') || el.getAttribute('data-name') ||
                    (titleEl ? titleEl.textContent.trim() : null);
    const name    = ownId || parentId;

    if (tag === 'g') {
      for (const child of el.children) walk(child, m, color, ownId || parentId);
      return;
    }

    // --- Rect ---
    if (tag === 'rect') {
      const x=+el.getAttribute('x')||0, y=+el.getAttribute('y')||0;
      const w=+el.getAttribute('width')||0, h=+el.getAttribute('height')||0;
      if (w<2||h<2) return;
      const corners = [[x,y],[x+w,y],[x+w,y+h],[x,y+h]].map(([px,py])=>applyM(m,px,py));
      const b = bbox(corners);
      const cW = (b.maxX-b.minX)*scale, cH = (b.maxY-b.minY)*scale;
      if (cW<MIN_PX||cH<MIN_PX) return;
      // Stays a rect if transform doesn't rotate it
      if (isIdentityScale(m)) {
        sections.push({
          name: name||`Section ${idx++}`, color: color||'#3B82F6',
          shape_type:'rect', shape_data:null,
          position_x:cx(b.minX), position_y:cy(b.minY), width:cW, height:cH,
        });
      } else {
        const pts = corners.map(p=>({ x:cx(p.x), y:cy(p.y) }));
        const sb  = bbox(pts);
        sections.push({
          name: name||`Section ${idx++}`, color: color||'#3B82F6',
          shape_type:'polygon', shape_data:{ points:pts },
          position_x:sb.minX, position_y:sb.minY,
          width:sb.maxX-sb.minX, height:sb.maxY-sb.minY,
        });
      }
      return;
    }

    // --- Circle / Ellipse ---
    if (tag === 'circle' || tag === 'ellipse') {
      const ccx = +el.getAttribute('cx')||0, ccy = +el.getAttribute('cy')||0;
      const rx = +(el.getAttribute('r')||el.getAttribute('rx'))||0;
      const ry = +(el.getAttribute('ry')||el.getAttribute('r'))||rx;
      if (rx<2) return;
      const centre = applyM(m, ccx, ccy);
      const cRx = rx*scale, cRy = ry*scale;
      if (cRx*2<MIN_PX) return;
      sections.push({
        name: name||`Section ${idx++}`, color: color||'#3B82F6',
        shape_type:'circle', shape_data:null,
        position_x:cx(centre.x)-cRx, position_y:cy(centre.y)-cRy,
        width:cRx*2, height:cRy*2,
      });
      return;
    }

    // --- Polygon / Polyline ---
    if (tag === 'polygon' || tag === 'polyline') {
      const raw = (el.getAttribute('points')||'').trim().split(/[\s,]+/).map(Number);
      const pts = [];
      for (let i=0;i+1<raw.length;i+=2) pts.push(applyM(m,raw[i],raw[i+1]));
      if (pts.length<3) return;
      const b = bbox(pts);
      if ((b.maxX-b.minX)*scale<MIN_PX||(b.maxY-b.minY)*scale<MIN_PX) return;
      const canvasPts = pts.map(p=>({ x:cx(p.x), y:cy(p.y) }));
      const sb = bbox(canvasPts);
      sections.push({
        name: name||`Section ${idx++}`, color: color||'#3B82F6',
        shape_type:'polygon', shape_data:{ points:canvasPts },
        position_x:sb.minX, position_y:sb.minY,
        width:sb.maxX-sb.minX, height:sb.maxY-sb.minY,
      });
      return;
    }

    // --- Path ---
    if (tag === 'path') {
      const d = el.getAttribute('d');
      if (!d) return;
      const raw = samplePath(d);
      if (raw.length<3) return;
      const pts = raw.map(p=>applyM(m,p.x,p.y));
      const b   = bbox(pts);
      if ((b.maxX-b.minX)*scale<MIN_PX||(b.maxY-b.minY)*scale<MIN_PX) return;
      const canvasPts = pts.map(p=>({ x:cx(p.x), y:cy(p.y) }));
      const sb = bbox(canvasPts);
      sections.push({
        name: name||`Section ${idx++}`, color: color||'#3B82F6',
        shape_type:'polygon', shape_data:{ points:canvasPts },
        position_x:sb.minX, position_y:sb.minY,
        width:sb.maxX-sb.minX, height:sb.maxY-sb.minY,
      });
    }
  }

  for (const child of svgEl.children) walk(child, ID, '#3B82F6', null);
  return sections;
}

// ── Component ──────────────────────────────────────────────────────────────────
export default function SeatMapEditor() {
  const { eventId: paramEventId, templateId } = useParams();
  const isTemplateMode = !!templateId;
  const mapId  = templateId || paramEventId;
  const eventId = paramEventId; // keep for nav / category loading
  const svc    = isTemplateMode ? adminSeatMapService : seatMapService;
  const navigate = useNavigate();
  const svgRef      = useRef(null);

  // ── Remote data ──────────────────────────────────────────────────────────────
  const [map,        setMap]        = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [newMapName, setNewMapName] = useState('');
  const [templates,  setTemplates]  = useState(null);   // null = not loaded yet
  const [loadingTpl, setLoadingTpl] = useState(false);
  const [applyingTpl, setApplyingTpl] = useState(false);

  // ── History (undo / redo) ────────────────────────────────────────────────────
  // Single atomic state prevents stale-closure / concurrent-mode null corruption
  const [hist, setHist] = useState({ past: [], present: { sections: [], objects: [] }, future: [] });
  const past    = hist.past;
  const present = hist.present;
  const future  = hist.future;
  const preDrag = useRef(null); // snapshot before current drag

  const sections = present?.sections ?? [];

  // Drop-in replacement for all existing setPresent(val) and setPresent(fn) calls
  function setPresent(valOrFn) {
    setHist(h => ({ ...h, present: typeof valOrFn === 'function' ? valOrFn(h.present) : valOrFn }));
  }

  // ── Viewport ─────────────────────────────────────────────────────────────────
  const [zoom, setZoom] = useState(1);
  const [pan,  setPan]  = useState({ x: 60, y: 60 });
  const spaceDown   = useRef(false); // Space-bar held → pan mode (ref = no re-render on move)
  const [spaceCursor, setSpaceCursor] = useState(false); // triggers cursor re-render only

  // ── Tool + Selection ─────────────────────────────────────────────────────────
  const [tool,       setTool]       = useState('select');
  const [selSecId,   setSelSecId]   = useState(null);          // primary section (panel / editSeats)
  const [selSecIds,  setSelSecIds]  = useState(new Set());     // all selected section IDs
  const [selSeatIds, setSelSeatIds] = useState(new Set()); // Set<seatId>
  const [editSeats,  setEditSeats]  = useState(false);
  const [bgSelected, setBgSelected] = useState(false);         // background image selected

  // ── 2-Level view ─────────────────────────────────────────────────────────────
  const [viewMode,     setViewMode]     = useState('map');     // 'map' | 'detail'
  const [detailSecId,  setDetailSecId]  = useState(null);
  const [savedMapView, setSavedMapView] = useState(null);      // { zoom, pan }
  const [transitioning, setTransitioning] = useState(false);

  // ── Draw ─────────────────────────────────────────────────────────────────────
  const [drawPrev,  setDrawPrev]  = useState(null);
  const [polyPts,   setPolyPts]   = useState([]);
  const [polyMouse, setPolyMouse] = useState(null);
  const [rubber,    setRubber]    = useState(null);

  // ── Editor opts ──────────────────────────────────────────────────────────────
  const [showGrid,   setShowGrid]   = useState(true);
  const [snapGrid,   setSnapGrid]   = useState(false);
  const [showLabels, setShowLabels] = useState(true);

  // ── Seat generation ──────────────────────────────────────────────────────────
  const [gen,         setGen]         = useState({ count: '50', startSeat: '1', density: 'normal' });
  const [genBusy,     setGenBusy]     = useState(false);
  const [genMode,     setGenMode]     = useState('auto'); // 'auto' | 'rows'
  const [rowConfig,   setRowConfig]   = useState({ totalRows: '10', defaultSeats: '100', overrides: [] });
  const [rowDirMode,  setRowDirMode]  = useState('auto');   // 'auto' | 'manual'
  const [rowDirAngle, setRowDirAngle] = useState('0');      // degrees (manual override)

  // ── Bulk UI ──────────────────────────────────────────────────────────────────
  const [bulkPrice,    setBulkPrice]    = useState('');
  const [relabelStart, setRelabelStart] = useState('');
  const [relabelDir,   setRelabelDir]   = useState('ltr');

  // ── SVG Import ───────────────────────────────────────────────────────────────
  const [svgModal,     setSvgModal]     = useState(false);
  const [svgParsed,    setSvgParsed]    = useState(null);   // array | null
  const [svgDragOver,  setSvgDragOver]  = useState(false);
  const [svgClearExisting, setSvgClearExisting] = useState(false);
  const svgFileRef = useRef(null);

  // ── Refs ─────────────────────────────────────────────────────────────────────
  const drag         = useRef(null);
  const dirtySeats   = useRef({});   // { seatId: { x, y, ... } } — positions/props to PATCH
  const deletedSeats = useRef({});   // { seatId: sectionId } — seats to DELETE on save
  const lastClick    = useRef(0);
  const keyHandler   = useRef(null); // always-current keyboard handler

  // ── Derived ──────────────────────────────────────────────────────────────────
  const selSection    = sections.find(s => String(s.id) === String(selSecId)) ?? null;
  const detailSection = sections.find(s => String(s.id) === String(detailSecId)) ?? null;

  // ══ HISTORY ═══════════════════════════════════════════════════════════════════

  function commit(next) {
    setHist(h => ({
      past:    [...h.past.slice(-MAX_HIST), h.present],
      present: next,
      future:  [],
    }));
  }

  function commitPresent() {
    setHist(h => ({
      past:    [...h.past.slice(-MAX_HIST), h.present],
      present: h.present,
      future:  [],
    }));
  }

  function finishDrag() {
    const snap = preDrag.current;
    if (!snap) return;
    preDrag.current = null;
    setHist(h => ({
      ...h,
      past:   [...h.past.slice(-MAX_HIST), snap],
      future: [],
    }));
  }

  function undo() {
    setHist(h => {
      if (!h.past.length) return h;
      const prev = h.past[h.past.length - 1];
      if (prev == null) return { ...h, past: h.past.slice(0, -1) };
      return {
        past:    h.past.slice(0, -1),
        present: prev,
        future:  [h.present, ...h.future],
      };
    });
    setSelSeatIds(new Set());
  }

  function redo() {
    setHist(h => {
      if (!h.future.length) return h;
      return {
        past:    [...h.past, h.present],
        present: h.future[0],
        future:  h.future.slice(1),
      };
    });
    setSelSeatIds(new Set());
  }

  // ══ 2-LEVEL VIEW ══════════════════════════════════════════════════════════════

  function enterDetailView(sec) {
    if (!sec || !sec.has_seats || sec.shape_data?.isObject) return;
    const svgEl = svgRef.current;
    if (!svgEl) return;
    const r = svgEl.getBoundingClientRect();
    const PADDING = 80;
    // Use editor dims (may be larger than section dims when seat count demands it)
    const { editorW, editorH } = inferEditorDims(sec);
    const targetZoom = clamp(
      Math.min((r.width - PADDING * 2) / editorW, (r.height - PADDING * 2) / editorH),
      0.05, 8
    );
    const targetPan = {
      x: r.width  / 2 - (sec.position_x + editorW / 2) * targetZoom,
      y: r.height / 2 - (sec.position_y + editorH / 2) * targetZoom,
    };
    setSavedMapView({ zoom, pan });
    setTransitioning(true);
    setTimeout(() => setTransitioning(false), 450);
    setDetailSecId(String(sec.id));
    setViewMode('detail');
    setSelSecId(sec.id);
    setSelSecIds(new Set([String(sec.id)]));
    setEditSeats(true);
    setSelSeatIds(new Set());
    setZoom(targetZoom);
    setPan(targetPan);
  }

  function findDuplicateLabels(section) {
    const seats = (section?.seats || []).filter(s => s.label);
    const seen  = new Set();
    const dupes = new Set();
    for (const s of seats) {
      if (seen.has(s.label)) dupes.add(s.label);
      else seen.add(s.label);
    }
    return dupes;
  }

  function exitDetailView() {
    if (detailSection) {
      const dupes = findDuplicateLabels(detailSection);
      if (dupes.size > 0) {
        toast.error(
          `"${detailSection.name}" has duplicate seat labels. Fix them before going back.`,
          { autoClose: 5000 }
        );
        return;
      }
    }
    setTransitioning(true);
    setTimeout(() => setTransitioning(false), 450);
    setViewMode('map');
    setDetailSecId(null);
    setEditSeats(false);
    setSelSeatIds(new Set());
    if (savedMapView) {
      setZoom(savedMapView.zoom);
      setPan(savedMapView.pan);
      setSavedMapView(null);
    }
  }

  // ══ SECTION OPERATIONS ════════════════════════════════════════════════════════

  function patchSec(id, patch, addHist = false) {
    const next = { ...present, sections: sections.map(s => String(s.id) === String(id) ? { ...s, ...patch } : s) };
    if (addHist) commit(next);
    else setPresent(next);
  }

  // Move a section to an absolute position, shifting polygon points by the same delta.
  function moveSec(id, newX, newY, addHist = false) {
    const sec = sections.find(s => String(s.id) === String(id));
    if (!sec) return;
    const dx = newX - sec.position_x, dy = newY - sec.position_y;
    const patch = { position_x: newX, position_y: newY };
    if (sec.shape_type === 'polygon' && sec.shape_data?.points) {
      patch.shape_data = { ...sec.shape_data, points: sec.shape_data.points.map(p => ({ x: p.x + dx, y: p.y + dy })) };
    }
    patchSec(id, patch, addHist);
  }

  function addSection(data) {
    const id  = tmpId();
    const sec = { id, seats: [], rotation: 0, ...data };
    commit({ ...present, sections: [...sections, sec] });
    setSelSecId(id); setSelSecIds(new Set([String(id)])); setSelSeatIds(new Set()); setEditSeats(false);
    return id;
  }

  function removeSection(id) {
    commit({ ...present, sections: sections.filter(s => String(s.id) !== String(id)) });
    setSelSecIds(prev => { const n = new Set(prev); n.delete(String(id)); return n; });
    if (String(selSecId) === String(id)) { setSelSecId(null); setSelSeatIds(new Set()); setEditSeats(false); }
  }

  function deleteSelectedSections() {
    if (selSecIds.size === 0) return;
    commit({ ...present, sections: sections.filter(s => !selSecIds.has(String(s.id))) });
    setSelSecId(null); setSelSecIds(new Set()); setSelSeatIds(new Set()); setEditSeats(false);
  }

  function lockSelectedSections(lock) {
    commit({ ...present, sections: sections.map(s => selSecIds.has(String(s.id)) ? { ...s, is_locked: lock } : s) });
  }

  function duplicateSection(id) {
    const orig = sections.find(s => String(s.id) === String(id));
    if (!orig) return;
    const newId = tmpId();
    const dup   = {
      ...orig,
      id:          newId,
      name:        orig.name + ' (copy)',
      position_x:  orig.position_x + 40,
      position_y:  orig.position_y + 40,
      shape_data:  orig.shape_type === 'polygon' && orig.shape_data?.points
        ? { ...orig.shape_data, points: orig.shape_data.points.map(p => ({ x: p.x + 40, y: p.y + 40 })) }
        : orig.shape_data,
      seats:       (orig.seats || []).map(seat => ({ ...seat, id: tmpId() })),
    };
    commit({ ...present, sections: [...sections, dup] });
    setSelSecId(newId); setSelSecIds(new Set([String(newId)])); setSelSeatIds(new Set()); setEditSeats(false);
  }

  function duplicateSelectedSections() {
    if (selSecIds.size === 0) return;
    const selected = sections.filter(s => selSecIds.has(String(s.id)));
    const newSecs  = selected.map(orig => ({
      ...orig,
      id:         tmpId(),
      name:       orig.name + ' (copy)',
      position_x: orig.position_x + 40,
      position_y: orig.position_y + 40,
      shape_data: orig.shape_type === 'polygon' && orig.shape_data?.points
        ? { ...orig.shape_data, points: orig.shape_data.points.map(p => ({ x: p.x + 40, y: p.y + 40 })) }
        : orig.shape_data,
      seats: (orig.seats || []).map(seat => ({ ...seat, id: tmpId() })),
    }));
    const newIds = new Set(newSecs.map(s => String(s.id)));
    commit({ ...present, sections: [...sections, ...newSecs] });
    setSelSecId(newSecs[0].id);
    setSelSecIds(newIds);
    setSelSeatIds(new Set());
    setEditSeats(false);
  }

  function selectAllSections() {
    if (sections.length === 0) return;
    setSelSecId(sections[0].id);
    setSelSecIds(new Set(sections.map(s => String(s.id))));
    setSelSeatIds(new Set());
    setEditSeats(false);
  }

  // ══ SEAT OPERATIONS ═══════════════════════════════════════════════════════════

  function patchSeat(secId, seatId, patch, addHist = false) {
    // Track any change to a real (non-tmp) seat so save flushes it
    if (!isTmp(seatId)) {
      dirtySeats.current[seatId] = { ...(dirtySeats.current[seatId] || {}), ...patch };
    }
    const next = {
      ...present,
      sections: sections.map(s =>
        String(s.id) !== String(secId) ? s : {
          ...s,
          seats: (s.seats || []).map(seat =>
            String(seat.id) === String(seatId) ? { ...seat, ...patch } : seat
          ),
        }
      ),
    };
    if (addHist) commit(next);
    else setPresent(next);
  }

  function patchSelectedSeats(patch) {
    if (!selSection || selSeatIds.size === 0) return;
    const secId = selSection.id;
    // Mark every real selected seat dirty
    for (const seatId of selSeatIds) {
      if (!isTmp(seatId)) {
        dirtySeats.current[seatId] = { ...(dirtySeats.current[seatId] || {}), ...patch };
      }
    }
    commit({
      ...present,
      sections: sections.map(s =>
        String(s.id) !== String(secId) ? s : {
          ...s,
          seats: (s.seats || []).map(seat => selSeatIds.has(seat.id) ? { ...seat, ...patch } : seat),
        }
      ),
    });
  }

  function deleteSelectedSeats() {
    if (!selSection || selSeatIds.size === 0) return;
    const secId = selSection.id;
    // Track real (non-tmp) seat IDs so we can call deleteSeat on save
    for (const seatId of selSeatIds) {
      if (!isTmp(seatId)) {
        deletedSeats.current[seatId] = secId;
        // Remove from dirty tracking — no point patching a deleted seat
        delete dirtySeats.current[seatId];
      }
    }
    commit({
      ...present,
      sections: sections.map(s =>
        String(s.id) !== String(secId) ? s : {
          ...s,
          seats: (s.seats || []).filter(seat => !selSeatIds.has(seat.id)),
        }
      ),
    });
    setSelSeatIds(new Set());
  }

  function addSeatAt(secId, localX, localY) {
    const sec = sections.find(s => String(s.id) === String(secId));
    if (!sec?.has_seats) return;
    const nums   = (sec.seats || []).map(s => parseInt(s.seat_number)).filter(n => !isNaN(n));
    const nextN  = nums.length ? Math.max(...nums) + 1 : 1;
    const newSeat = { id: tmpId(), x: localX, y: localY, row: '', seat_number: String(nextN), label: String(nextN), status: 'available', rotation: 0, price_override: null };
    commit({
      ...present,
      sections: sections.map(s =>
        String(s.id) !== String(secId) ? s : { ...s, seats: [...(s.seats || []), newSeat] }
      ),
    });
    setSelSeatIds(new Set([newSeat.id]));
  }

  // ── Align / distribute selected seats ────────────────────────────────────────
  function alignSeats(axis, mode) {
    if (!selSection || selSeatIds.size < 2) return;
    const sel = (selSection.seats || []).filter(s => selSeatIds.has(s.id));
    const vals = sel.map(s => axis === 'x' ? s.x : s.y);
    const ref  = mode === 'min' ? Math.min(...vals) : mode === 'max' ? Math.max(...vals) : (Math.min(...vals) + Math.max(...vals)) / 2;
    for (const s of sel) {
      if (!isTmp(s.id)) dirtySeats.current[s.id] = { ...(dirtySeats.current[s.id] || {}), [axis]: ref };
    }
    const nextSeats = (selSection.seats || []).map(s =>
      selSeatIds.has(s.id) ? { ...s, [axis]: ref } : s
    );
    commit({ ...present, sections: sections.map(s => String(s.id) === String(selSection.id) ? { ...s, seats: nextSeats } : s) });
  }

  function distributeSeats(axis) {
    if (!selSection || selSeatIds.size < 2) return;
    const sel = (selSection.seats || []).filter(s => selSeatIds.has(s.id));
    if (sel.length < 2) return;

    if (axis === 'x') {
      const avgY   = sel.reduce((sum, s) => sum + s.y, 0) / sel.length;
      const sorted = [...sel].sort((a, b) => a.x - b.x);
      const lo     = sorted[0].x;
      const step   = sorted.length > 1 ? (sorted[sorted.length - 1].x - lo) / (sorted.length - 1) : 0;
      const posMap = new Map(sorted.map((s, i) => [s.id, lo + i * step]));
      for (const [id, x] of posMap) {
        if (!isTmp(id)) dirtySeats.current[id] = { ...(dirtySeats.current[id] || {}), x, y: avgY };
      }
      const nextSeats = (selSection.seats || []).map(s =>
        posMap.has(s.id) ? { ...s, x: posMap.get(s.id), y: avgY } : s
      );
      commit({ ...present, sections: sections.map(s => String(s.id) === String(selSection.id) ? { ...s, seats: nextSeats } : s) });
    } else {
      const avgX   = sel.reduce((sum, s) => sum + s.x, 0) / sel.length;
      const sorted = [...sel].sort((a, b) => a.y - b.y);
      const lo     = sorted[0].y;
      const step   = sorted.length > 1 ? (sorted[sorted.length - 1].y - lo) / (sorted.length - 1) : 0;
      const posMap = new Map(sorted.map((s, i) => [s.id, lo + i * step]));
      for (const [id, y] of posMap) {
        if (!isTmp(id)) dirtySeats.current[id] = { ...(dirtySeats.current[id] || {}), x: avgX, y };
      }
      const nextSeats = (selSection.seats || []).map(s =>
        posMap.has(s.id) ? { ...s, x: avgX, y: posMap.get(s.id) } : s
      );
      commit({ ...present, sections: sections.map(s => String(s.id) === String(selSection.id) ? { ...s, seats: nextSeats } : s) });
    }
  }

  function numberSelectedSeats(startN = 1, rtl = false) {
    if (!selSection || selSeatIds.size === 0) return;
    const sel    = (selSection.seats || []).filter(s => selSeatIds.has(s.id));
    const sorted = [...sel].sort((a, b) => rtl ? b.x - a.x : a.x - b.x);
    const num    = new Map(sorted.map((s, i) => [s.id, startN + i]));
    const nextSeats = (selSection.seats || []).map(s => {
      if (!num.has(s.id)) return s;
      const n = num.get(s.id);
      if (!isTmp(s.id)) dirtySeats.current[s.id] = { ...(dirtySeats.current[s.id] || {}), seat_number: String(n), label: `${s.row || ''}${n}` };
      return { ...s, seat_number: String(n), label: `${s.row || ''}${n}` };
    });
    commit({ ...present, sections: sections.map(s => String(s.id) === String(selSection.id) ? { ...s, seats: nextSeats } : s) });
  }

  function relabelSelectedSeats(startLabel, direction) {
    if (!selSection || selSeatIds.size === 0) return;
    const match = startLabel.trim().match(/^([A-Za-z]*)(\d+)$/);
    if (!match) { toast.error('Label must end with a number, e.g. B3 or 12'); return; }
    const rowPrefix = match[1];
    const startNum  = parseInt(match[2], 10);

    const sel = (selSection.seats || []).filter(s => selSeatIds.has(s.id));

    const sorted = [...sel].sort((a, b) => {
      if (direction === 'ltr') return a.x - b.x || a.y - b.y;
      if (direction === 'rtl') return b.x - a.x || a.y - b.y;
      if (direction === 'ttb') return a.y - b.y || a.x - b.x;
      if (direction === 'btt') return b.y - a.y || a.x - b.x;
      return 0;
    });

    const nextSeats = (selSection.seats || []).map(s => {
      const idx = sorted.findIndex(ss => ss.id === s.id);
      if (idx < 0) return s;
      const seatNum = startNum + idx;
      const label   = `${rowPrefix}${seatNum}`;
      const patch   = { label, row: rowPrefix || s.row, seat_number: String(seatNum) };
      if (!isTmp(s.id)) dirtySeats.current[s.id] = { ...(dirtySeats.current[s.id] || {}), ...patch };
      return { ...s, ...patch };
    });

    commit({
      ...present,
      sections: sections.map(s =>
        String(s.id) === String(selSection.id) ? { ...s, seats: nextSeats } : s
      ),
    });
  }

  // ══ RESOLVE LAYOUT ════════════════════════════════════════════════════════════
  // Saves all sections, resolves tmp IDs → real DB IDs, updates state.
  // Returns { updatedSections, tmpToReal } so callers can find their new IDs.

  async function resolveLayout() {
    const payload = sections.map(s => ({
      ...(isTmp(s.id) ? {} : { id: s.id }),
      name:               s.name,
      shape_type:         s.shape_type,
      shape_data:         s.shape_data || null,
      has_seats:          !!s.has_seats,
      capacity:           s.capacity  || null,
      ticket_category_id: s.ticket_category_id || null,
      default_price:      (s.default_price !== '' && s.default_price != null) ? s.default_price : null,
      position_x:         s.position_x,
      position_y:         s.position_y,
      width:              s.width,
      height:             s.height,
      rotation:           s.rotation || 0,
      color:              s.color,
      status:             'active',
      is_locked:          !!s.is_locked,
    }));

    const res   = await svc.saveLayout(mapId, payload);
    const saved = res.data?.map || res.data?.seat_map;

    if (!saved?.sections) return { updatedSections: sections, tmpToReal: {} };

    setMap(m => ({ ...m, ...saved }));
    const savedSecs  = saved.sections;
    const existingIds = new Set(sections.filter(s => !isTmp(s.id)).map(s => String(s.id)));
    const newlySaved  = savedSecs.filter(s => !existingIds.has(String(s.id)));

    const tmpToReal = {};
    let newIdx = 0;
    const updatedSections = sections.map(localSec => {
      if (!isTmp(localSec.id)) return localSec;
      const resolved = newlySaved[newIdx++];
      if (resolved) tmpToReal[String(localSec.id)] = String(resolved.id);
      return resolved ? { ...localSec, id: resolved.id } : localSec;
    });

    setPresent(p => ({ ...p, sections: updatedSections }));

    // Keep selection in sync when selected section IDs were resolved
    setSelSecId(prev  => (prev  && isTmp(prev))  ? (tmpToReal[String(prev)]  ?? prev)  : prev);
    setSelSecIds(prev => new Set([...prev].map(id => tmpToReal[id] ?? id)));

    return { updatedSections, tmpToReal };
  }

  // ══ GENERATE SEATS ════════════════════════════════════════════════════════════

  async function handleGenerateSeats() {
    if (!selSection?.has_seats) return;
    setGenBusy(true);
    try {
      let sec = selSection;

      if (isTmp(sec.id)) {
        const { updatedSections, tmpToReal } = await resolveLayout();
        const resolvedId = tmpToReal[String(sec.id)];
        if (!resolvedId) { toast.error('Failed to save section'); return; }
        sec = updatedSections.find(s => String(s.id) === resolvedId) || { ...sec, id: resolvedId };
      }

      const spacing    = DENSITY_SPACING[gen.density] || DENSITY_SPACING.normal;
      const manualAngle = rowDirMode === 'manual' ? (parseFloat(rowDirAngle) || 0) : null;
      let positions, editorW, editorH, effectiveSpacing;

      if (genMode === 'rows') {
        const totalRows    = Math.max(1, parseInt(rowConfig.totalRows) || 10);
        const defaultSeats = Math.max(0, parseInt(rowConfig.defaultSeats) || 0);
        const rowCounts = [];
        for (let r = 1; r <= totalRows; r++) {
          const ov = rowConfig.overrides.find(o => r >= parseInt(o.fromRow || 1) && r <= parseInt(o.toRow || 1));
          rowCounts.push(ov ? Math.max(0, parseInt(ov.seats) || 0) : defaultSeats);
        }
        ({ positions, editorW, editorH, effectiveSpacing } = generateSeatsForSection(sec, 'rows', { rowCounts }, spacing, manualAngle));
      } else {
        const totalSeats = Math.max(1, parseInt(gen.count) || 50);
        const startSeat  = Math.max(1, parseInt(gen.startSeat) || 1);
        ({ positions, editorW, editorH, effectiveSpacing } = generateSeatsForSection(sec, 'auto', { totalSeats, startSeat }, spacing, manualAngle));
      }

      if (positions.length === 0) {
        toast.error('Could not generate any seats — check section geometry.');
        return;
      }

      const seatSize = Math.max(3, Math.round(spacing * 0.42));
      const res = await svc.generateSeats(mapId, sec.id, {
        rows:             [...new Set(positions.map(p => p.row))],
        seats_per_row:    Math.max(...[...new Set(positions.map(p => p.row))].map(r => positions.filter(p2 => p2.row === r).length)),
        seat_size:        seatSize,
        seat_gap:         Math.max(1, Math.round(spacing - seatSize)),
        replace_all:      true,
        custom_positions: positions,
      });

      const allSeats = res.data?.seats || [];
      // Persist editor dims + spacings in shape_data so they survive save+reload exactly.
      // _effective_spacing is the actual min seat-to-seat distance; used for seat radius.
      const updatedShapeData = {
        ...(sec.shape_data || {}),
        _editorW: editorW, _editorH: editorH,
        _seat_spacing: spacing,
        _effective_spacing: effectiveSpacing,
      };
      patchSec(sec.id, {
        seats: allSeats, shape_data: updatedShapeData,
        _seat_spacing: spacing, _effective_spacing: effectiveSpacing,
        _editorW: editorW, _editorH: editorH,
      }, true);
      toast.success(`Generated ${allSeats.length} seats`);

      const updatedSec = {
        ...sec, seats: allSeats, shape_data: updatedShapeData,
        _seat_spacing: spacing, _effective_spacing: effectiveSpacing,
        _editorW: editorW, _editorH: editorH,
      };
      if (viewMode !== 'detail') enterDetailView(updatedSec);
      else {
        // Already in detail view — re-fit viewport to potentially larger editor
        const r = svgRef.current?.getBoundingClientRect();
        if (r) {
          const PAD = 80;
          const nz = clamp(Math.min((r.width - PAD*2) / editorW, (r.height - PAD*2) / editorH), 0.05, 8);
          setTransitioning(true);
          setTimeout(() => setTransitioning(false), 400);
          setZoom(nz);
          setPan({ x: r.width/2 - (sec.position_x + editorW/2) * nz, y: r.height/2 - (sec.position_y + editorH/2) * nz });
        }
      }
    } catch (err) {
      toast.error('Failed to generate seats');
    } finally { setGenBusy(false); }
  }

  // ══ LOAD ══════════════════════════════════════════════════════════════════════

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [mRes, cRes] = await Promise.allSettled([
          svc.getMap(mapId),
          isTemplateMode ? Promise.resolve(null) : organizerService.getEvent(eventId),
        ]);
        if (!alive) return;
        if (mRes.status === 'fulfilled') {
          const d = mRes.value?.data;
          const m = d?.map || d?.seat_map;
          if (m) {
            setMap({ ...m, background_image_url: storageUrl(m.background_image_url) });
            setPresent({ sections: (m.sections || []).map(s => ({ ...s, seats: s.seats || [] })), objects: [] });
          }
        }
        if (cRes.status === 'fulfilled' && cRes.value) setCategories(cRes.value?.data?.ticket_categories || []);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [mapId]);

  // ══ SAVE ══════════════════════════════════════════════════════════════════════

  // ── SVG Import handlers ───────────────────────────────────────────────────────
  function handleSvgFile(file) {
    if (!file || !file.name.toLowerCase().endsWith('.svg')) {
      toast.error('Please upload a .svg file'); return;
    }
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const parsed = parseSVGIntoSections(e.target.result, map.width, map.height);
        if (!parsed.length) { toast.warn('No recognisable shapes found in this SVG'); return; }
        setSvgParsed(parsed);
      } catch (err) {
        toast.error('Could not parse SVG: ' + err.message);
      }
    };
    reader.readAsText(file);
  }

  function doSvgImport() {
    if (!svgParsed?.length) return;
    const base = svgClearExisting ? [] : sections;
    const newSecs = svgParsed.map(s => ({
      id: tmpId(),
      seats: [],
      rotation: 0,
      has_seats: false,
      capacity: null,
      ticket_category_id: null,
      price_zone_id: null,
      default_price: '',
      status: 'active',
      is_locked: false,
      ...s,
    }));
    commit({ ...present, sections: [...base, ...newSecs] });
    setSvgModal(false);
    setSvgParsed(null);
    setSvgClearExisting(false);
    toast.success(`Imported ${newSecs.length} section${newSecs.length !== 1 ? 's' : ''} from SVG`);
  }

  async function handleSave() {
    // Validate no duplicate labels across all sections before saving
    const dupErrors = [];
    for (const sec of sections) {
      if (!sec.has_seats) continue;
      const dupes = findDuplicateLabels(sec);
      if (dupes.size > 0) dupErrors.push(`"${sec.name}"`);
    }
    if (dupErrors.length > 0) {
      toast.error(
        `Duplicate seat labels in: ${dupErrors.join(', ')}. Fix them before saving.`,
        { autoClose: 6000 }
      );
      return;
    }

    setSaving(true);
    try {
      // 1. Save all sections and resolve tmp IDs → real DB IDs
      const { updatedSections } = await resolveLayout();

      // 2. Build seatId → sectionId lookup (real seats only)
      const seatToSec = {};
      for (const sec of updatedSections) {
        for (const seat of (sec.seats || [])) {
          if (!isTmp(seat.id)) seatToSec[String(seat.id)] = String(sec.id);
        }
      }

      // 3. Collect all pending seat operations into one batch request
      const toCreate = [];
      for (const sec of updatedSections) {
        for (const seat of (sec.seats || [])) {
          if (!isTmp(seat.id)) continue;
          toCreate.push({
            tmp_id:         seat.id,
            section_id:     sec.id,
            row:            seat.row            || '',
            seat_number:    seat.seat_number,
            label:          seat.label,
            x:              seat.x,
            y:              seat.y,
            rotation:       seat.rotation       || 0,
            price_zone_id:  seat.price_zone_id  ?? null,
            price_override: seat.price_override ?? null,
            is_handicapped: seat.is_handicapped ?? false,
            status:         seat.status         || 'available',
          });
        }
      }

      const toDeleteIds = Object.keys(deletedSeats.current).map(Number);
      deletedSeats.current = {};

      const dirty = { ...dirtySeats.current };
      dirtySeats.current = {};
      const toUpdate = Object.entries(dirty)
        .filter(([sid]) => seatToSec[sid])
        .map(([sid, payload]) => ({ id: Number(sid), ...payload }));

      // 4. Send one batch request for all seat operations
      if (toCreate.length > 0 || toDeleteIds.length > 0 || toUpdate.length > 0) {
        const res = await svc.batchSeats(mapId, {
          create: toCreate,
          update: toUpdate,
          delete: toDeleteIds,
        });

        // Resolve tmp seat IDs → real IDs in state
        const createdList = res.data?.created || [];
        if (createdList.length > 0) {
          const tmpSeatMap = Object.fromEntries(createdList.map(({ tmp_id, seat }) => [tmp_id, seat]));
          setPresent(p => ({
            ...p,
            sections: p.sections.map(s => ({
              ...s,
              seats: (s.seats || []).map(seat =>
                tmpSeatMap[seat.id] ? { ...seat, id: tmpSeatMap[seat.id].id } : seat
              ),
            })),
          }));
        }
      }

      toast.success('Layout saved');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  }


  async function handleCreateMap(e) {
    e.preventDefault();
    try {
      const res = await seatMapService.createMap(eventId, { name: newMapName, width: 1200, height: 800 });
      const m   = res.data?.map || res.data?.seat_map;
      setMap(m); setPresent({ sections: [], objects: [] });
      toast.success('Seat map created');
    } catch { toast.error('Failed to create map'); }
  }

  async function loadTemplates() {
    if (templates !== null) return;
    setLoadingTpl(true);
    try {
      const res = await adminSeatMapService.listForOrganizer();
      setTemplates(res.data?.templates || []);
    } catch { toast.error('Failed to load templates'); setTemplates([]); }
    finally { setLoadingTpl(false); }
  }

  async function handleApplyTemplate(templateId) {
    setApplyingTpl(true);
    try {
      const res = await adminSeatMapService.applyToEvent(eventId, templateId);
      const m   = res.data?.map;
      if (m) {
        setMap({ ...m, background_image_url: storageUrl(m.background_image_url) });
        setPresent({ sections: (m.sections || []).map(s => ({ ...s, seats: s.seats || [] })), objects: [] });
        toast.success('Template applied — you can now customise the map');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to apply template');
    } finally { setApplyingTpl(false); }
  }

  async function handlePublish() {
    try {
      const newStatus = map?.status === 'published' ? 'draft' : 'published';
      await svc.updateMap(mapId, { status: newStatus });
      setMap(m => ({ ...m, status: newStatus }));
      toast.success(newStatus === 'published' ? 'Map published' : 'Reverted to draft');
    } catch { toast.error('Failed to update status'); }
  }

  // ══ WHEEL ZOOM ═══════════════════════════════════════════════════════════════

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const fn = e => {
      e.preventDefault();
      const factor = e.deltaY > 0 ? 0.9 : 1.1;
      const r = el.getBoundingClientRect();
      const mx = e.clientX - r.left, my = e.clientY - r.top;
      setZoom(z => {
        const nz = clamp(z * factor, 0.12, 8);
        setPan(p => ({ x: mx - (mx - p.x) * (nz / z), y: my - (my - p.y) * (nz / z) }));
        return nz;
      });
    };
    el.addEventListener('wheel', fn, { passive: false });
    return () => el.removeEventListener('wheel', fn);
  }, []);

  // ══ KEYBOARD ══════════════════════════════════════════════════════════════════

  keyHandler.current = e => {
    const tag = e.target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

    // Space held = pan mode (Figma-style). Track via ref so no re-render.
    if (e.key === ' ') {
      if (e.type === 'keydown' && !e.repeat) { spaceDown.current = true; setSpaceCursor(true); e.preventDefault(); }
      if (e.type === 'keyup') { spaceDown.current = false; setSpaceCursor(false); }
      return;
    }

    if (e.key === 'Escape') {
      setPolyPts([]); setPolyMouse(null);
      setBgSelected(false);
      if (tool === 'polygon' || tool === 'rect' || tool === 'circle' || tool === 'addseat') setTool('select');
      if (viewMode === 'detail') { exitDetailView(); return; }
      if (editSeats) { setEditSeats(false); setSelSeatIds(new Set()); }
      else { setSelSecId(null); setSelSecIds(new Set()); setSelSeatIds(new Set()); }
      return;
    }

    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      if (selSeatIds.size > 0) { deleteSelectedSeats(); return; }
      if (selSecIds.size > 1)  { deleteSelectedSections(); return; }
      if (selSecId)            { removeSection(selSecId); }
      return;
    }

    if (e.metaKey || e.ctrlKey) {
      if (e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); return; }
      if ((e.key === 'z' && e.shiftKey) || e.key === 'y') { e.preventDefault(); redo(); return; }
      if (e.key === 'l' || e.key === 'L') {
        e.preventDefault();
        if (selSecIds.size > 1) {
          const allLocked = sections.filter(s => selSecIds.has(String(s.id))).every(s => s.is_locked);
          lockSelectedSections(!allLocked);
        } else if (selSection) {
          patchSec(selSection.id, { is_locked: !selSection.is_locked }, true);
        }
        return;
      }
      if (e.key === 'd') {
        e.preventDefault();
        if (selSecIds.size > 1) duplicateSelectedSections();
        else if (selSecId) duplicateSection(selSecId);
        return;
      }
      if (e.key === 'a') {
        e.preventDefault();
        if (editSeats && selSection) {
          setSelSeatIds(new Set((selSection.seats || []).map(s => s.id)));
        } else {
          selectAllSections();
        }
        return;
      }
    }

    // Arrow nudge
    if (['ArrowLeft','ArrowRight','ArrowUp','ArrowDown'].includes(e.key)) {
      e.preventDefault();
      const d  = e.shiftKey ? 10 : 1;
      const dx = e.key === 'ArrowLeft' ? -d : e.key === 'ArrowRight' ? d : 0;
      const dy = e.key === 'ArrowUp'   ? -d : e.key === 'ArrowDown'  ? d : 0;
      if (selSeatIds.size > 0 && selSection) {
        const sec      = selSection;
        const nextSeats = (sec.seats || []).map(s => selSeatIds.has(s.id) ? { ...s, x: s.x + dx, y: s.y + dy } : s);
        commit({ ...present, sections: sections.map(s => String(s.id) === String(sec.id) ? { ...s, seats: nextSeats } : s) });
      } else if (selSecId && selSection) {
        moveSec(selSecId, selSection.position_x + dx, selSection.position_y + dy, true);
      }
    }
  };

  useEffect(() => {
    const fn = e => keyHandler.current?.(e);
    window.addEventListener('keydown', fn);
    window.addEventListener('keyup', fn);
    return () => {
      window.removeEventListener('keydown', fn);
      window.removeEventListener('keyup', fn);
    };
  }, []);

  // ══ SVG COORDINATE HELPER ═════════════════════════════════════════════════════

  const wp = e => toWorld(e, svgRef.current, zoom, pan);

  // ══ RESIZE HANDLE POSITIONS ═══════════════════════════════════════════════════

  function resizeHandles(sec) {
    const { position_x: px, position_y: py, width: w, height: h } = sec;
    return [
      { id: 'tl', x: px,       y: py,       cur: 'nwse-resize' },
      { id: 'tc', x: px+w/2,   y: py,       cur: 'ns-resize'   },
      { id: 'tr', x: px+w,     y: py,       cur: 'nesw-resize'  },
      { id: 'ml', x: px,       y: py+h/2,   cur: 'ew-resize'   },
      { id: 'mr', x: px+w,     y: py+h/2,   cur: 'ew-resize'   },
      { id: 'bl', x: px,       y: py+h,     cur: 'nesw-resize'  },
      { id: 'bc', x: px+w/2,   y: py+h,     cur: 'ns-resize'   },
      { id: 'br', x: px+w,     y: py+h,     cur: 'nwse-resize' },
    ];
  }

  // ══ MOUSE DOWN HANDLERS ═══════════════════════════════════════════════════════

  function onBgMouseDown(e) {
    if (e.button !== 0) return;


    if (tool === 'polygon') return;

    if (tool === 'select') {
      // Space held → pan anywhere without disturbing selection
      if (spaceDown.current) {
        drag.current = { type: 'pan', sx: e.clientX - pan.x, sy: e.clientY - pan.y };
        return;
      }
      // Detail view: background click pans (section polygon is handled by onSecMouseDown)
      if (viewMode === 'detail') {
        drag.current = { type: 'pan', sx: e.clientX - pan.x, sy: e.clientY - pan.y };
        return;
      }
      if (editSeats && selSection) {
        // Rubber-band selection within seat-edit mode
        const p = wp(e);
        preDrag.current = present;
        drag.current = { type: 'rubber', x0: p.x, y0: p.y };
        setRubber({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
        if (!e.ctrlKey && !e.metaKey) setSelSeatIds(new Set());
      } else {
        // Map mode: pan and clear selection
        setBgSelected(false);
        setSelSecId(null); setSelSecIds(new Set()); setSelSeatIds(new Set());
        drag.current = { type: 'pan', sx: e.clientX - pan.x, sy: e.clientY - pan.y };
      }
      return;
    }

    if (tool === 'addseat') {
      if (selSection?.has_seats) {
        const p = wp(e);
        addSeatAt(selSection.id, p.x - selSection.position_x, p.y - selSection.position_y);
      }
      return;
    }

    // rect / circle draw
    const p = wp(e);
    preDrag.current = present;
    drag.current    = { type: 'draw', x0: p.x, y0: p.y };
    setDrawPrev({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
  }

  function onSecMouseDown(e, sec) {
    if (tool !== 'select') return;
    e.stopPropagation();

    // Space held → pan, regardless of what was clicked
    if (spaceDown.current) {
      drag.current = { type: 'pan', sx: e.clientX - pan.x, sy: e.clientY - pan.y };
      return;
    }

    // In detail view, clicking inside the section (on empty space, not a seat)
    // starts rubber-band seat selection — same as editSeats mode on the map.
    if (viewMode === 'detail' && String(sec.id) === String(detailSecId)) {
      const p = wp(e);
      preDrag.current = present;
      drag.current = { type: 'rubber', x0: p.x, y0: p.y };
      setRubber({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
      if (!e.ctrlKey && !e.metaKey) setSelSeatIds(new Set());
      return;
    }

    if (editSeats && String(sec.id) === String(selSecId)) {
      // Clicking empty space inside the active section starts rubber-band seat selection
      const p = wp(e);
      preDrag.current = present;
      drag.current = { type: 'rubber', x0: p.x, y0: p.y };
      setRubber({ x0: p.x, y0: p.y, x1: p.x, y1: p.y });
      if (!e.ctrlKey && !e.metaKey) setSelSeatIds(new Set());
      return;
    }
    const strId = String(sec.id);

    if (e.ctrlKey || e.metaKey) {
      // Toggle section in/out of multi-selection — no drag
      setSelSeatIds(new Set()); setEditSeats(false);
      setSelSecId(sec.id);
      setSelSecIds(prev => {
        const next = new Set(prev);
        if (next.has(strId)) next.delete(strId); else next.add(strId);
        return next;
      });
      return;
    }

    // Locked sections: allow selection click, but no drag
    if (sec.is_locked) {
      setBgSelected(false);
      setSelSeatIds(new Set()); setEditSeats(false);
      setSelSecId(sec.id); setSelSecIds(new Set([strId]));
      return;
    }

    setBgSelected(false);
    setSelSeatIds(new Set()); setEditSeats(false);

    // If clicking a section that's already in a multi-selection, drag all together (skip locked)
    const dragIds = (selSecIds.has(strId) && selSecIds.size > 1)
      ? [...selSecIds].filter(id => { const s = sections.find(x => String(x.id) === id); return s && !s.is_locked; })
      : [strId];

    if (!selSecIds.has(strId) || selSecIds.size === 1) {
      setSelSecId(sec.id);
      setSelSecIds(new Set([strId]));
    }

    const p = wp(e);
    preDrag.current = present;
    const origins = {};
    for (const id of dragIds) {
      const s = sections.find(x => String(x.id) === String(id));
      if (s) origins[id] = {
        ox: s.position_x, oy: s.position_y,
        origPoints: (s.shape_type === 'polygon' && s.shape_data?.points) ? s.shape_data.points : null,
      };
    }
    drag.current = { type: 'sec', id: sec.id, ids: dragIds, x0: p.x, y0: p.y, origins };
  }

  function onSeatMouseDown(e, seat, sec) {
    if (tool !== 'select') return;
    e.stopPropagation();
    const isAlready = selSeatIds.has(seat.id);
    if (e.ctrlKey || e.metaKey) {
      const next = new Set(selSeatIds);
      if (isAlready) next.delete(seat.id); else next.add(seat.id);
      setSelSeatIds(next);
      return;
    }
    if (!isAlready) setSelSeatIds(new Set([seat.id]));
    const finalIds = isAlready ? selSeatIds : new Set([seat.id]);
    const p = wp(e);
    const origins = {};
    for (const sid of finalIds) {
      const s = (sec.seats || []).find(x => x.id === sid);
      if (s) origins[sid] = { x: s.x, y: s.y };
    }
    preDrag.current = present;
    drag.current    = { type: 'seats', secId: sec.id, ids: [...finalIds], x0: p.x, y0: p.y, origins, secRot: sec.rotation || 0 };
  }

  function onResizeHandleDown(e, sec, hid) {
    e.stopPropagation();
    const p = wp(e);
    preDrag.current = present;
    drag.current    = {
      type: 'resize', id: sec.id, handle: hid,
      x0: p.x, y0: p.y,
      ox: sec.position_x, oy: sec.position_y, ow: sec.width, oh: sec.height,
    };
  }

  function onPolyHandleDown(e, sec, ptIdx) {
    e.stopPropagation();
    const p = wp(e);
    preDrag.current = present;
    drag.current = {
      type:   'poly-vertex',
      id:     sec.id,
      ptIdx,
      x0:     p.x,
      y0:     p.y,
      origPt: { ...sec.shape_data.points[ptIdx] },
      secRot: sec.rotation || 0,
    };
  }

  // dxSign/dySign: +1 = handle moves in positive axis to make bigger, -1 = opposite, 0 = axis fixed
  const POLY_SCALE_H = {
    tl: { dxSign: -1, dySign: -1 },  tc: { dxSign:  0, dySign: -1 },
    tr: { dxSign: +1, dySign: -1 },  ml: { dxSign: -1, dySign:  0 },
    mr: { dxSign: +1, dySign:  0 },  bl: { dxSign: -1, dySign: +1 },
    bc: { dxSign:  0, dySign: +1 },  br: { dxSign: +1, dySign: +1 },
  };

  function onPolyScaleHandleDown(e, sec, hid) {
    e.stopPropagation();
    const p  = wp(e);
    const { position_x: px, position_y: py, width: w, height: h } = sec;
    const { dxSign, dySign } = POLY_SCALE_H[hid];
    // Anchor = the opposite corner/edge that stays fixed during scale
    const ax = dxSign >= 0 ? px      : px + w;
    const ay = dySign >= 0 ? py      : py + h;
    preDrag.current = present;
    drag.current = {
      type:       'poly-scale',
      id:         sec.id,
      x0: p.x,   y0: p.y,
      origPoints: (sec.shape_data?.points || []).map(pt => ({ ...pt })),
      origW:      w,
      origH:      h,
      dxSign,     dySign,
      anchor:     { x: ax, y: ay },
      secRot:     sec.rotation || 0,
    };
  }

  function onRotHandleDown(e, sec) {
    e.stopPropagation();
    const cx = sec.position_x + sec.width  / 2;
    const cy = sec.position_y + sec.height / 2;
    preDrag.current = present;
    drag.current    = { type: 'rotate', id: sec.id, cx, cy };
  }

  function onRotSeatGroupHandleDown(e, sec) {
    e.stopPropagation();
    const selectedSeats = (sec.seats || []).filter(s => selSeatIds.has(s.id));
    if (selectedSeats.length === 0) return;

    // Centroid in section-local space
    const localCx = selectedSeats.reduce((sum, s) => sum + s.x, 0) / selectedSeats.length;
    const localCy = selectedSeats.reduce((sum, s) => sum + s.y, 0) / selectedSeats.length;

    // World centroid — apply section rotation
    const preCx = sec.position_x + localCx;
    const preCy = sec.position_y + localCy;
    const secRot = sec.rotation || 0;
    let worldCx = preCx, worldCy = preCy;
    if (secRot !== 0) {
      const scx = sec.position_x + sec.width / 2;
      const scy = sec.position_y + sec.height / 2;
      const rad = secRot * Math.PI / 180;
      const dx = preCx - scx, dy = preCy - scy;
      worldCx = Math.cos(rad) * dx - Math.sin(rad) * dy + scx;
      worldCy = Math.sin(rad) * dx + Math.cos(rad) * dy + scy;
    }

    const p = wp(e);
    const startAngle = r2d(Math.atan2(p.y - worldCy, p.x - worldCx)) + 90;

    const basePositions = {};
    for (const s of selectedSeats) {
      basePositions[s.id] = { x: s.x, y: s.y, rotation: s.rotation || 0 };
    }

    preDrag.current = present;
    drag.current = { type: 'rotate-seat-group', secId: sec.id, localCx, localCy, worldCx, worldCy, startAngle, basePositions };
  }

  function onBgImgMouseDown(e) {
    if (tool !== 'select') return;
    e.stopPropagation();
    setBgSelected(true);
    setSelSecId(null); setSelSecIds(new Set()); setSelSeatIds(new Set()); setEditSeats(false);
    const p = wp(e);
    const bgX = map.bg_x ?? 0;
    const bgY = map.bg_y ?? 0;
    const bgW = map.bg_width ?? map.width;
    const bgH = map.bg_height ?? map.height;
    drag.current = { type: 'bg-move', x0: p.x, y0: p.y, origX: bgX, origY: bgY, origW: bgW, origH: bgH, curX: bgX, curY: bgY };
  }

  function onBgResizeHandleDown(e, handle) {
    e.stopPropagation();
    const p = wp(e);
    const bgX = map.bg_x ?? 0;
    const bgY = map.bg_y ?? 0;
    const bgW = map.bg_width ?? map.width;
    const bgH = map.bg_height ?? map.height;
    drag.current = { type: 'bg-resize', x0: p.x, y0: p.y, handle, ox: bgX, oy: bgY, ow: bgW, oh: bgH, curX: bgX, curY: bgY, curW: bgW, curH: bgH };
  }

  async function handleBgUpload(e) {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    try {
      const res = await svc.uploadBackground(mapId, file);
      setMap(m => ({ ...m, background_image: res.data.path, background_image_url: storageUrl(res.data.url), bg_x: 0, bg_y: 0, bg_width: null, bg_height: null, bg_opacity: 0.5 }));
      setBgSelected(false);
      toast.success('Background uploaded');
    } catch { toast.error('Upload failed'); }
  }

  async function removeBackground() {
    try {
      await svc.updateMap(mapId, { background_image: null, bg_x: null, bg_y: null, bg_width: null, bg_height: null });
      setMap(m => ({ ...m, background_image: null, background_image_url: null, bg_x: null, bg_y: null, bg_width: null, bg_height: null }));
      setBgSelected(false);
      toast.success('Background removed');
    } catch { toast.error('Failed to remove background'); }
  }

  // ══ MOUSE MOVE ════════════════════════════════════════════════════════════════

  function onMouseMove(e) {
    if (tool === 'polygon') { setPolyMouse(wp(e)); return; }
    const dr = drag.current;
    if (!dr) return;

    if (dr.type === 'pan') {
      setPan({ x: e.clientX - dr.sx, y: e.clientY - dr.sy });
      return;
    }

    const p = wp(e);


    if (dr.type === 'draw') {
      setDrawPrev({ x0: dr.x0, y0: dr.y0, x1: p.x, y1: p.y });
      return;
    }

    if (dr.type === 'bg-move') {
      const nx = dr.origX + (p.x - dr.x0);
      const ny = dr.origY + (p.y - dr.y0);
      dr.curX = nx; dr.curY = ny;
      setMap(m => ({ ...m, bg_x: nx, bg_y: ny }));
      return;
    }

    if (dr.type === 'bg-resize') {
      const dx = p.x - dr.x0, dy = p.y - dr.y0;
      const h = dr.handle;
      let nx = dr.ox, ny = dr.oy, nw = dr.ow, nh = dr.oh;
      if (h.includes('l')) { nx = dr.ox + dx; nw = Math.max(40, dr.ow - dx); }
      if (h.includes('r')) { nw = Math.max(40, dr.ow + dx); }
      if (h.includes('t')) { ny = dr.oy + dy; nh = Math.max(40, dr.oh - dy); }
      if (h.includes('b')) { nh = Math.max(40, dr.oh + dy); }
      dr.curX = nx; dr.curY = ny; dr.curW = nw; dr.curH = nh;
      setMap(m => ({ ...m, bg_x: nx, bg_y: ny, bg_width: nw, bg_height: nh }));
      return;
    }

    if (dr.type === 'rubber') {
      setRubber({ x0: dr.x0, y0: dr.y0, x1: p.x, y1: p.y });
      if (selSection) {
        const found = seatsInRect({ x0: dr.x0, y0: dr.y0, x1: p.x, y1: p.y }, selSection.seats || [], selSection);
        setSelSeatIds(found);
      }
      return;
    }

    if (dr.type === 'sec') {
      const wdx = p.x - dr.x0, wdy = p.y - dr.y0;
      setPresent(prev => ({
        ...prev,
        sections: prev.sections.map(s => {
          const origin = dr.origins?.[String(s.id)];
          if (!origin) return s;
          const nx = snapV(origin.ox + wdx, snapGrid);
          const ny = snapV(origin.oy + wdy, snapGrid);
          const patch = { ...s, position_x: nx, position_y: ny };
          if (origin.origPoints) {
            const adx = nx - origin.ox, ady = ny - origin.oy;
            patch.shape_data = { ...s.shape_data, points: origin.origPoints.map(pt => ({ x: pt.x + adx, y: pt.y + ady })) };
          }
          return patch;
        }),
      }));
      return;
    }

    if (dr.type === 'seats') {
      // Transform world-space delta into section-local space by applying inverse section rotation
      const wdx = p.x - dr.x0, wdy = p.y - dr.y0;
      let ldx = wdx, ldy = wdy;
      if (dr.secRot !== 0) {
        const rad = dr.secRot * Math.PI / 180;
        const cos = Math.cos(rad), sin = Math.sin(rad);
        ldx =  cos * wdx + sin * wdy;
        ldy = -sin * wdx + cos * wdy;
      }
      setPresent(prev => ({
        ...prev,
        sections: prev.sections.map(s => {
          if (String(s.id) !== String(dr.secId)) return s;
          return {
            ...s,
            seats: (s.seats || []).map(seat => {
              if (!dr.ids.includes(seat.id)) return seat;
              const o = dr.origins[seat.id];
              return o ? { ...seat, x: o.x + ldx, y: o.y + ldy } : seat;
            }),
          };
        }),
      }));
      return;
    }

    if (dr.type === 'poly-scale') {
      // Inverse-rotate the world-space delta into the polygon's local space
      let dx = p.x - dr.x0, dy = p.y - dr.y0;
      if (dr.secRot !== 0) {
        const rad = dr.secRot * Math.PI / 180;
        const cos = Math.cos(rad), sin = Math.sin(rad);
        [dx, dy] = [cos * dx + sin * dy, -sin * dx + cos * dy];
      }
      const sx = dr.dxSign !== 0 ? Math.max(0.05, 1 + dr.dxSign * dx / dr.origW) : 1;
      const sy = dr.dySign !== 0 ? Math.max(0.05, 1 + dr.dySign * dy / dr.origH) : 1;
      const { x: ax, y: ay } = dr.anchor;
      const newPts = dr.origPoints.map(pt => ({
        x: ax + (pt.x - ax) * sx,
        y: ay + (pt.y - ay) * sy,
      }));
      const xs = newPts.map(pt => pt.x), ys = newPts.map(pt => pt.y);
      setPresent(prev => ({
        ...prev,
        sections: prev.sections.map(s => {
          if (String(s.id) !== String(dr.id)) return s;
          return {
            ...s,
            shape_data: { ...s.shape_data, points: newPts },
            position_x: Math.min(...xs),
            position_y: Math.min(...ys),
            width:  Math.max(40, Math.max(...xs) - Math.min(...xs)),
            height: Math.max(40, Math.max(...ys) - Math.min(...ys)),
          };
        }),
      }));
      return;
    }

    if (dr.type === 'poly-vertex') {
      // Apply inverse section rotation so the handle tracks the mouse correctly
      let dx = p.x - dr.x0, dy = p.y - dr.y0;
      if (dr.secRot !== 0) {
        const rad = dr.secRot * Math.PI / 180;
        const cos = Math.cos(rad), sin = Math.sin(rad);
        const rx = cos * dx + sin * dy;
        const ry = -sin * dx + cos * dy;
        dx = rx; dy = ry;
      }
      const nx = snapV(dr.origPt.x + dx, snapGrid);
      const ny = snapV(dr.origPt.y + dy, snapGrid);
      setPresent(prev => ({
        ...prev,
        sections: prev.sections.map(s => {
          if (String(s.id) !== String(dr.id)) return s;
          const pts = s.shape_data.points.map((pt, i) =>
            i === dr.ptIdx ? { x: nx, y: ny } : pt
          );
          const xs = pts.map(pt => pt.x), ys = pts.map(pt => pt.y);
          return {
            ...s,
            shape_data: { ...s.shape_data, points: pts },
            position_x: Math.min(...xs),
            position_y: Math.min(...ys),
            width:  Math.max(40, Math.max(...xs) - Math.min(...xs)),
            height: Math.max(40, Math.max(...ys) - Math.min(...ys)),
          };
        }),
      }));
      return;
    }

    if (dr.type === 'resize') {
      const dx = p.x - dr.x0, dy = p.y - dr.y0;
      const h  = dr.handle;
      let nx = dr.ox, ny = dr.oy, nw = dr.ow, nh = dr.oh;
      if (h.includes('l')) { nx = dr.ox + dx; nw = Math.max(40, dr.ow - dx); }
      if (h.includes('r')) { nw = Math.max(40, dr.ow + dx); }
      if (h.includes('t')) { ny = dr.oy + dy; nh = Math.max(40, dr.oh - dy); }
      if (h.includes('b')) { nh = Math.max(40, dr.oh + dy); }
      setPresent(prev => ({
        ...prev,
        sections: prev.sections.map(s =>
          String(s.id) === String(dr.id) ? { ...s, position_x: nx, position_y: ny, width: nw, height: nh } : s
        ),
      }));
      return;
    }

    if (dr.type === 'rotate') {
      const angle = r2d(Math.atan2(p.y - dr.cy, p.x - dr.cx)) + 90;
      setPresent(prev => ({
        ...prev,
        sections: prev.sections.map(s =>
          String(s.id) === String(dr.id) ? { ...s, rotation: Math.round(angle) } : s
        ),
      }));
      return;
    }

    if (dr.type === 'rotate-seat-group') {
      const currentAngle = r2d(Math.atan2(p.y - dr.worldCy, p.x - dr.worldCx)) + 90;
      const delta    = currentAngle - dr.startAngle;
      const deltaRad = delta * Math.PI / 180;
      const cos = Math.cos(deltaRad), sin = Math.sin(deltaRad);
      setPresent(prev => ({
        ...prev,
        sections: prev.sections.map(s =>
          String(s.id) !== String(dr.secId) ? s : {
            ...s,
            seats: (s.seats || []).map(seat => {
              const base = dr.basePositions[seat.id];
              if (!base) return seat;
              const dx = base.x - dr.localCx, dy = base.y - dr.localCy;
              return {
                ...seat,
                x:        cos * dx - sin * dy + dr.localCx,
                y:        sin * dx + cos * dy + dr.localCy,
                rotation: (base.rotation + Math.round(delta)) % 360,
              };
            }),
          }
        ),
      }));
    }
  }

  // ══ MOUSE UP ══════════════════════════════════════════════════════════════════

  function onMouseUp(e) {
    const dr = drag.current;
    drag.current = null;
    setDrawPrev(null);
    setRubber(null);

    if (!dr) return;


    if (dr.type === 'rubber' || dr.type === 'pan') { preDrag.current = null; return; }

    if (dr.type === 'bg-move') {
      svc.updateMap(mapId, { bg_x: dr.curX, bg_y: dr.curY })
        .catch(err => console.warn('Failed to save bg position', err));
      return;
    }

    if (dr.type === 'bg-resize') {
      svc.updateMap(mapId, { bg_x: dr.curX, bg_y: dr.curY, bg_width: dr.curW, bg_height: dr.curH })
        .catch(err => console.warn('Failed to save bg size', err));
      return;
    }

    if (dr.type === 'seats') {
      // Mark dirty seats
      const liveSec = present.sections.find(s => String(s.id) === String(dr.secId));
      if (liveSec) {
        for (const sid of dr.ids) {
          const seat = (liveSec.seats || []).find(s => s.id === sid);
          if (seat && !isTmp(sid)) {
            dirtySeats.current[sid] = { x: seat.x, y: seat.y };
          }
        }
      }
    }

    if (dr.type === 'rotate-seat-group') {
      const liveSec = present.sections.find(s => String(s.id) === String(dr.secId));
      if (liveSec) {
        for (const seatId of Object.keys(dr.basePositions)) {
          const seat = (liveSec.seats || []).find(s => String(s.id) === String(seatId));
          if (seat && !isTmp(seat.id)) {
            dirtySeats.current[seat.id] = { ...(dirtySeats.current[seat.id] || {}), x: seat.x, y: seat.y, rotation: seat.rotation };
          }
        }
      }
    }

    if (dr.type === 'draw') {
      const p = wp(e);
      const x = snapV(Math.min(dr.x0, p.x), snapGrid);
      const y = snapV(Math.min(dr.y0, p.y), snapGrid);
      const w = Math.max(Math.abs(p.x - dr.x0), 80);
      const h = Math.max(Math.abs(p.y - dr.y0), 60);
      addSection({
        name:               `Section ${sections.length + 1}`,
        shape_type:         tool === 'circle' ? 'circle' : 'rect',
        shape_data:         null,
        has_seats:          false,
        capacity:           null,
        ticket_category_id: null,
        default_price:      '',
        position_x: x, position_y: y, width: w, height: h,
        color: PALETTE[sections.length % PALETTE.length],
      });
      setTool('select');
      preDrag.current = null;
      return;
    }

    finishDrag();
  }

  // ══ POLYGON ═══════════════════════════════════════════════════════════════════

  function onCanvasClick(e) {
    if (tool !== 'polygon') return;
    const now = Date.now();
    if (now - lastClick.current < 280) return;
    lastClick.current = now;
    const p = wp(e);
    if (polyPts.length >= 3 && d2(p, polyPts[0]) < 16 / zoom) { closePoly(polyPts); return; }
    setPolyPts(prev => [...prev, p]);
  }

  function onCanvasDblClick(e) {
    if (tool !== 'polygon') return;
    const pts = polyPts.length > 0 ? polyPts.slice(0, -1) : polyPts;
    if (pts.length < 3) { toast.warning('Need at least 3 points'); return; }
    closePoly(pts);
  }

  function closePoly(pts) {
    const xs = pts.map(p => p.x), ys = pts.map(p => p.y);
    addSection({
      name:               `Section ${sections.length + 1}`,
      shape_type:         'polygon',
      shape_data:         { points: pts },
      has_seats:          false,
      capacity:           null,
      ticket_category_id: null,
      default_price:      '',
      position_x:         Math.min(...xs),
      position_y:         Math.min(...ys),
      width:              Math.max(...xs) - Math.min(...xs),
      height:             Math.max(...ys) - Math.min(...ys),
      color:              PALETTE[sections.length % PALETTE.length],
    });
    setPolyPts([]); setPolyMouse(null); setTool('select');
  }

  // ══ ADD VENUE OBJECT ══════════════════════════════════════════════════════════

  function addObject(type) {
    const labels = { stage: 'Stage', entrance: 'Entrance', exit: 'Exit', bar: 'Bar', vip: 'VIP Area', text: 'Label' };
    const colors = { stage: '#1F2937', entrance: '#059669', exit: '#DC2626', bar: '#D97706', vip: '#7C3AED', text: '#6B7280' };
    addSection({
      name:       labels[type] || type,
      shape_type: 'rect',
      shape_data: { isObject: true, objectType: type },
      has_seats:  false,
      capacity:   null, ticket_category_id: null, default_price: null,
      position_x: 100, position_y: 100,
      width:  type === 'text' ? 120 : 200,
      height: type === 'text' ? 36  : 60,
      color:  colors[type] || '#6B7280',
    });
  }

  // ══ RENDERING ═════════════════════════════════════════════════════════════════

  function renderSection(sec) {
    const strId      = String(sec.id);
    const isSel      = selSecIds.has(strId);
    const isSingleSel = isSel && selSecIds.size === 1;
    const isDetailActive = viewMode === 'detail' && strId === String(detailSecId);
    const isDimmed   = viewMode === 'detail' && !isDetailActive;

    const { position_x: px, position_y: py, width: w, height: h, color: c, rotation: rot = 0 } = sec;
    const cx = px + w / 2, cy = py + h / 2;
    const isObj = sec.shape_data?.isObject;

    // In detail mode, the editor workspace may be larger than the original section dimensions.
    // Scale the shape to fill the editor canvas so seats remain visually inside the outline.
    const { editorW, editorH } = isDetailActive ? inferEditorDims(sec) : { editorW: w, editorH: h };
    const scaleX = editorW / w, scaleY = editorH / h;

    const fill   = { fill: c, fillOpacity: isObj ? 0.75 : 0.18 };
    const stroke = { stroke: c, strokeWidth: isSel ? 2.5 : 1.5, strokeDasharray: isSel ? '7 3' : undefined };

    let shape;
    if (sec.shape_type === 'circle') {
      shape = <ellipse cx={editorW/2} cy={editorH/2} rx={editorW/2} ry={editorH/2} {...fill} {...stroke} />;
    } else if (sec.shape_type === 'polygon' && sec.shape_data?.points?.length) {
      const pts = sec.shape_data.points.map(p => `${(p.x - px) * scaleX},${(p.y - py) * scaleY}`).join(' ');
      shape = <polygon points={pts} {...fill} {...stroke} />;
    } else {
      shape = <rect x={0} y={0} width={editorW} height={editorH} rx={6} {...fill} {...stroke} />;
    }

    // ── Dimmed ghost in detail mode ───────────────────────────────────────────
    if (isDimmed) {
      return (
        <g key={sec.id} transform={rot ? `rotate(${rot},${cx},${cy})` : undefined}
          opacity={0.2} pointerEvents="none">
          <g transform={`translate(${px},${py})`}>
            {shape}
            <text x={8} y={16} fontSize={11} fill={isObj ? 'white' : c} fontWeight="700"
              pointerEvents="none" style={{ userSelect: 'none' }}>{sec.name}</text>
          </g>
        </g>
      );
    }

    // ── Active detail-mode section OR normal map-mode section ─────────────────
    const inEdit = isDetailActive || (viewMode === 'map' && isSel && editSeats);
    const seatCount = (sec.seats || []).length;

    return (
      <g key={sec.id}
        transform={rot ? `rotate(${rot},${cx},${cy})` : undefined}
        style={{ cursor: tool === 'select' ? (sec.is_locked ? 'default' : 'move') : 'default' }}
        onMouseDown={e => onSecMouseDown(e, sec)}
        onDoubleClick={e => {
          e.stopPropagation();
          if (viewMode === 'map' && sec.has_seats && !isObj) enterDetailView(sec);
        }}>

        <g transform={`translate(${px},${py})`}>
          {shape}

          {/* Map mode: label inside shape (no seats visible, so no conflict) */}
          {viewMode === 'map' && (
            <>
              <text x={8} y={16} fontSize={11} fill={isObj ? 'white' : c} fontWeight="700"
                pointerEvents="none" style={{ userSelect: 'none' }}>
                {sec.name}{!isObj && sec.default_price ? `  ·  QAR ${sec.default_price}` : ''}
              </text>
              {!sec.has_seats && !isObj && sec.capacity && (
                <text x={8} y={30} fontSize={9} fill={c} fillOpacity={0.6} pointerEvents="none" style={{ userSelect: 'none' }}>
                  Cap: {sec.capacity}
                </text>
              )}
            </>
          )}

          {/* Detail/seat mode: pill label floated ABOVE the section top edge */}
          {viewMode === 'detail' && isDetailActive && !isObj && (
            <g pointerEvents="none">
              <rect x={editorW/2 - 52} y={-24} width={104} height={18} rx={9}
                fill={c} fillOpacity={0.92} />
              <text x={editorW/2} y={-11} textAnchor="middle" fontSize={10} fill="white" fontWeight="700"
                style={{ userSelect: 'none' }}>
                {sec.name}{sec.default_price ? `  ·  QAR ${sec.default_price}` : ''}
              </text>
            </g>
          )}


          {/* Seat count badge — map mode only, when section has seats */}
          {viewMode === 'map' && sec.has_seats && seatCount > 0 && (
            <g pointerEvents="none">
              <rect x={w/2 - 24} y={h/2 - 11} width={48} height={22} rx={11} fill="rgba(0,0,0,0.52)" />
              <text x={w/2} y={h/2 + 5} textAnchor="middle" fontSize={10} fill="white" fontWeight="600">
                {seatCount} seats
              </text>
            </g>
          )}

          {/* Lock badge */}
          {sec.is_locked && (
            <g transform={`translate(${w - 18}, 4)`} pointerEvents="none">
              <rect width={14} height={14} rx={3} fill="rgba(0,0,0,0.45)" />
              <path d="M3.5,7 L3.5,4.8 A3.5,3.2 0 0 1 10.5,4.8 L10.5,7"
                fill="none" stroke="white" strokeWidth={1.4} strokeLinecap="round" />
              <rect x={2} y={6.5} width={10} height={7} rx={1.5} fill="white" />
              <circle cx={7} cy={9.5} r={1.3} fill="rgba(0,0,0,0.45)" />
              <rect x={6.3} y={9.5} width={1.4} height={2.2} rx={0.7} fill="rgba(0,0,0,0.45)" />
            </g>
          )}
        </g>

        {/* Seats — only in detail view (active) or while in legacy editSeats map mode */}
        {inEdit && seatCount > 0 && renderSeats(sec, true)}

        {/* Resize / poly handles — map mode only, not locked */}
        {viewMode === 'map' && isSingleSel && !editSeats && !sec.is_locked && sec.shape_type !== 'polygon' && renderResizeHandles(sec)}
        {viewMode === 'map' && isSingleSel && !editSeats && !sec.is_locked && sec.shape_type === 'polygon' && renderPolyHandles(sec)}

        {/* Rotation handle — map mode only, not locked */}
        {viewMode === 'map' && isSingleSel && !editSeats && !sec.is_locked && (
          <g>
            <line x1={cx} y1={py} x2={cx} y2={py - ROT_DIST}
              stroke="#3B82F6" strokeWidth={1} strokeDasharray="3 2" pointerEvents="none" />
            <circle cx={cx} cy={py - ROT_DIST} r={8}
              fill="#3B82F6" stroke="white" strokeWidth={1.5}
              style={{ cursor: 'alias' }}
              onMouseDown={e => onRotHandleDown(e, sec)} />
            <text x={cx} y={py - ROT_DIST + 4} textAnchor="middle"
              fontSize={10} fill="white" fontWeight="bold" pointerEvents="none">↻</text>
          </g>
        )}

        {/* "Edit Seats" button — map mode, selected, has seats, none already editing */}
        {viewMode === 'map' && isSingleSel && !editSeats && sec.has_seats && !isObj && (
          <g transform={`translate(${cx - 44}, ${py + h + 6})`}
            style={{ cursor: 'pointer' }}
            onMouseDown={e => { e.stopPropagation(); enterDetailView(sec); }}>
            <rect x={0} y={0} width={88} height={20} rx={10} fill={c} fillOpacity={0.9} />
            <text x={44} y={14} fontSize={10} fill="white" textAnchor="middle"
              fontWeight="600" pointerEvents="none">✏ Edit Seats</text>
          </g>
        )}

        {/* Seat count text label — map mode, when selected, not yet showing badge */}
        {viewMode === 'map' && isSingleSel && sec.has_seats && !editSeats && seatCount === 0 && (
          <text x={px + 8} y={py + h - 6} fontSize={9} fill={c} fillOpacity={0.6}
            pointerEvents="none" style={{ userSelect: 'none' }}>
            No seats yet
          </text>
        )}
      </g>
    );
  }

  function seatDisplayRadius(sec) {
    // Use the chosen seat spacing (same for both auto-fill and row-config) so
    // seat size is identical in both modes at the same density setting.
    const sp = sec._seat_spacing || sec.shape_data?._seat_spacing;
    if (sp && sp > 0) return Math.min(10, Math.max(3, sp * 0.42));
    const n = (sec.seats || []).length;
    if (!n) return 10;
    const estimatedSpacing = Math.sqrt((sec.width * sec.height) / n);
    return Math.min(10, Math.max(3, estimatedSpacing * 0.40));
  }

  function renderSeats(sec, interactive = true) {
    const r = seatDisplayRadius(sec);
    const labelFs = Math.max(4, Math.min(7, r * 0.7));

    const seatEls = (sec.seats || []).map(seat => {
      const isSeatSel = selSeatIds.has(seat.id);
      const col = isSeatSel ? '#FBBF24'
        : ({ available: '#22C55E', held: '#F59E0B', sold: '#EF4444', blocked: '#9CA3AF', disabled: '#D1D5DB' }[seat.status] ?? '#22C55E');
      const wx = sec.position_x + seat.x, wy = sec.position_y + seat.y;
      const seatRot = seat.rotation || 0;

      return (
        <g key={seat.id}
          transform={`rotate(${seatRot},${wx},${wy})`}
          style={{ cursor: interactive ? 'grab' : 'default' }}
          onMouseDown={interactive ? e => onSeatMouseDown(e, seat, sec) : undefined}>
          <circle cx={wx} cy={wy} r={r}
            fill={col}
            stroke={seat.is_handicapped ? '#2563EB' : (isSeatSel ? '#92400E' : '#fff')}
            strokeWidth={isSeatSel ? Math.max(1, r * 0.2) : Math.max(0.5, r * 0.08)} />
          {seat.is_handicapped ? (
            <text x={wx} y={wy + r * 0.38} textAnchor="middle" fontSize={Math.max(4, r * 0.85)}
              fill="#fff" pointerEvents="none" style={{ userSelect: 'none' }}>♿</text>
          ) : showLabels && r >= 5 && (
            <text x={wx} y={wy + r * 0.38} textAnchor="middle" fontSize={labelFs} fontWeight="700"
              fill={isSeatSel ? '#92400E' : '#fff'} pointerEvents="none" style={{ userSelect: 'none' }}>
              {seat.label || seat.seat_number}
            </text>
          )}
          {seat.price_override != null && (
            <circle cx={wx + r * 0.7} cy={wy - r * 0.7} r={Math.max(2, r * 0.35)}
              fill="#F59E0B" stroke="white" strokeWidth={0.5} pointerEvents="none" />
          )}
        </g>
      );
    });

    // Group rotation handle
    if (interactive && selSeatIds.size > 0) {
      const selSeats = (sec.seats || []).filter(s => selSeatIds.has(s.id));
      if (selSeats.length > 0) {
        const wxVals = selSeats.map(s => sec.position_x + s.x);
        const wyVals = selSeats.map(s => sec.position_y + s.y);
        const hcx = (Math.min(...wxVals) + Math.max(...wxVals)) / 2;
        const hTop = Math.min(...wyVals) - r;
        seatEls.push(
          <g key="__seat-rot-handle">
            <line x1={hcx} y1={hTop} x2={hcx} y2={hTop - ROT_DIST}
              stroke="#3B82F6" strokeWidth={1} strokeDasharray="3 2" pointerEvents="none" />
            <circle cx={hcx} cy={hTop - ROT_DIST} r={8}
              fill="#3B82F6" stroke="white" strokeWidth={1.5}
              style={{ cursor: 'alias' }}
              onMouseDown={e => onRotSeatGroupHandleDown(e, sec)} />
            <text x={hcx} y={hTop - ROT_DIST + 4} textAnchor="middle"
              fontSize={10} fill="white" fontWeight="bold" pointerEvents="none">↻</text>
          </g>
        );
      }
    }

    return seatEls;
  }

  function renderResizeHandles(sec) {
    return resizeHandles(sec).map(h => (
      <rect key={h.id}
        x={h.x - HANDLE_R} y={h.y - HANDLE_R} width={HANDLE_R * 2} height={HANDLE_R * 2} rx={2}
        fill="white" stroke="#3B82F6" strokeWidth={1.5}
        style={{ cursor: h.cur }}
        onMouseDown={e => { e.stopPropagation(); onResizeHandleDown(e, sec, h.id); }} />
    ));
  }

  function renderPolyHandles(sec) {
    const { position_x: px, position_y: py, width: w, height: h } = sec;

    // Dashed bounding-box outline so the user sees the scale frame
    const bbRect = (
      <rect key="pbb" x={px} y={py} width={w} height={h}
        fill="none" stroke="#F97316" strokeWidth={1} strokeDasharray="5 3"
        pointerEvents="none" />
    );

    // Orange squares at bounding-box corners/edges — drag to scale all vertices
    const scaleHandles = [
      { id: 'tl', x: px,     y: py,     cur: 'nwse-resize' },
      { id: 'tc', x: px+w/2, y: py,     cur: 'ns-resize'   },
      { id: 'tr', x: px+w,   y: py,     cur: 'nesw-resize'  },
      { id: 'ml', x: px,     y: py+h/2, cur: 'ew-resize'   },
      { id: 'mr', x: px+w,   y: py+h/2, cur: 'ew-resize'   },
      { id: 'bl', x: px,     y: py+h,   cur: 'nesw-resize'  },
      { id: 'bc', x: px+w/2, y: py+h,   cur: 'ns-resize'   },
      { id: 'br', x: px+w,   y: py+h,   cur: 'nwse-resize' },
    ].map(hh => (
      <rect key={`psh_${hh.id}`}
        x={hh.x - HANDLE_R} y={hh.y - HANDLE_R} width={HANDLE_R * 2} height={HANDLE_R * 2} rx={1}
        fill="#F97316" stroke="white" strokeWidth={1.5}
        style={{ cursor: hh.cur }}
        onMouseDown={e => { e.stopPropagation(); onPolyScaleHandleDown(e, sec, hh.id); }} />
    ));

    return [bbRect, ...scaleHandles];
  }

  function renderBgImage() {
    if (!map?.background_image_url) return null;
    const bgX = map.bg_x ?? 0;
    const bgY = map.bg_y ?? 0;
    const bgW = map.bg_width  ?? map.width;
    const bgH = map.bg_height ?? map.height;
    const opacity = map.bg_opacity ?? 0.5;
    const R = HANDLE_R;
    return (
      <g>
        <image
          href={storageUrl(map.background_image_url)}
          x={bgX} y={bgY} width={bgW} height={bgH}
          opacity={opacity}
          preserveAspectRatio="xMidYMid meet"
          style={{ cursor: tool === 'select' ? 'move' : 'default', userSelect: 'none' }}
          onMouseDown={onBgImgMouseDown}
        />
        {bgSelected && (
          <>
            <rect x={bgX} y={bgY} width={bgW} height={bgH}
              fill="none" stroke="#8B5CF6" strokeWidth={1.5} strokeDasharray="6 3"
              pointerEvents="none" />
            {[
              { id:'tl', cx:bgX,       cy:bgY,       cur:'nwse-resize' },
              { id:'tc', cx:bgX+bgW/2, cy:bgY,       cur:'ns-resize'   },
              { id:'tr', cx:bgX+bgW,   cy:bgY,       cur:'nesw-resize' },
              { id:'ml', cx:bgX,       cy:bgY+bgH/2, cur:'ew-resize'   },
              { id:'mr', cx:bgX+bgW,   cy:bgY+bgH/2, cur:'ew-resize'   },
              { id:'bl', cx:bgX,       cy:bgY+bgH,   cur:'nesw-resize' },
              { id:'bc', cx:bgX+bgW/2, cy:bgY+bgH,   cur:'ns-resize'   },
              { id:'br', cx:bgX+bgW,   cy:bgY+bgH,   cur:'nwse-resize' },
            ].map(hh => (
              <rect key={`bgh_${hh.id}`}
                x={hh.cx - R} y={hh.cy - R} width={R*2} height={R*2} rx={1}
                fill="#8B5CF6" stroke="white" strokeWidth={1.5}
                style={{ cursor: hh.cur }}
                onMouseDown={e => { e.stopPropagation(); onBgResizeHandleDown(e, hh.id); }} />
            ))}
          </>
        )}
      </g>
    );
  }

  function renderGrid() {
    if (!showGrid || !map) return null;
    const lines = [];
    for (let x = 0; x <= map.width;  x += GRID_PX) lines.push(<line key={`v${x}`} x1={x} y1={0} x2={x} y2={map.height} stroke="#E2E8F0" strokeWidth={0.4} />);
    for (let y = 0; y <= map.height; y += GRID_PX) lines.push(<line key={`h${y}`} x1={0} y1={y} x2={map.width} y2={y} stroke="#E2E8F0" strokeWidth={0.4} />);
    return <g pointerEvents="none">{lines}</g>;
  }

  function renderDrawPrev() {
    if (!drawPrev) return null;
    const { x0, y0, x1, y1 } = drawPrev;
    const x = Math.min(x0,x1), y = Math.min(y0,y1), rw = Math.abs(x1-x0), rh = Math.abs(y1-y0);
    const p = { fill: 'rgba(59,130,246,0.1)', stroke: '#3B82F6', strokeWidth: 1.5, strokeDasharray: '5 3' };
    return tool === 'circle'
      ? <ellipse cx={x+rw/2} cy={y+rh/2} rx={rw/2} ry={rh/2} {...p} />
      : <rect x={x} y={y} width={rw} height={rh} rx={5} {...p} />;
  }

  function renderPolyPrev() {
    if (!polyPts.length) return null;
    const live      = [...polyPts, ...(polyMouse ? [polyMouse] : [])];
    const nearClose = polyPts.length >= 3 && polyMouse && d2(polyMouse, polyPts[0]) < 16 / zoom;
    return (
      <g>
        <polyline points={live.map(p => `${p.x},${p.y}`).join(' ')} fill="none" stroke="#3B82F6" strokeWidth={1.5} strokeDasharray="5 3" />
        {live.length > 2 && polyMouse && (
          <line x1={polyMouse.x} y1={polyMouse.y} x2={polyPts[0].x} y2={polyPts[0].y}
            stroke="#3B82F6" strokeWidth={1} strokeDasharray="3 4" strokeOpacity={0.3} />
        )}
        {polyPts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={i === 0 ? 7 : 4}
            fill={i === 0 ? (nearClose ? '#10B981' : '#1D4ED8') : '#3B82F6'} stroke="#fff" strokeWidth={1.5} />
        ))}
        {nearClose && <circle cx={polyPts[0].x} cy={polyPts[0].y} r={14}
          fill="none" stroke="#10B981" strokeWidth={2} strokeDasharray="3 3" />}
      </g>
    );
  }

  // ══ RIGHT PANEL ═══════════════════════════════════════════════════════════════

  function renderPanel() {
    if (selSeatIds.size > 0 && selSection) return SeatPanel();
    if (selSecIds.size > 1) return MultiSectionPanel();
    if (selSection) return SectionPanel();
    return MapPanel();
  }

  function MultiSectionPanel() {
    return (
      <div className="p-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="fw-semibold mb-0" style={{ fontSize: 13 }}>{selSecIds.size} Sections selected</h6>
          <div className="d-flex gap-1">
            <button className="btn btn-sm btn-outline-primary p-1" title="Copy selected (Ctrl+D)"
              onClick={duplicateSelectedSections}>
              <i className="fas fa-copy" style={{ fontSize: 10 }} />
            </button>
            <button className="btn btn-sm btn-outline-danger p-1" title="Delete selected (Delete)"
              onClick={deleteSelectedSections}>
              <i className="fas fa-trash-alt" style={{ fontSize: 10 }} />
            </button>
          </div>
        </div>
        <div className="d-flex gap-1 mb-3">
          <button className="btn btn-sm btn-outline-warning flex-grow-1 py-1" style={{ fontSize: 11 }}
            onClick={() => lockSelectedSections(true)}>
            <i className="fas fa-lock me-1" style={{ fontSize: 10 }} />Lock all
          </button>
          <button className="btn btn-sm btn-outline-secondary flex-grow-1 py-1" style={{ fontSize: 11 }}
            onClick={() => lockSelectedSections(false)}>
            <i className="fas fa-unlock me-1" style={{ fontSize: 10 }} />Unlock all
          </button>
        </div>
        <p className="text-muted mb-3" style={{ fontSize: 12 }}>
          Drag any selected section to move all together.<br />
          Ctrl+click to add or remove sections.
        </p>
        <div className="d-flex gap-1">
          <button className="btn btn-sm btn-outline-secondary flex-grow-1"
            onClick={selectAllSections}>
            Select all
          </button>
          <button className="btn btn-sm btn-outline-secondary flex-grow-1"
            onClick={() => { setSelSecId(null); setSelSecIds(new Set()); }}>
            Deselect all
          </button>
        </div>
      </div>
    );
  }

  function MapPanel() {
    return (
      <div className="p-3">
        <h6 className="fw-semibold mb-3" style={{ fontSize: 13 }}>Map</h6>
        {map && (
          <>
            <PF label="Name"><span className="text-dark" style={{ fontSize: 13 }}>{map.name}</span></PF>
            <PF label="Status">
              <span className={`badge ${map.status === 'published' ? 'bg-success' : 'bg-secondary'}`}>
                {map.status === 'published' ? 'Published' : 'Draft'}
              </span>
            </PF>
            <PF label="Canvas">
              <div className="d-flex align-items-center gap-1">
                <input type="number" min="400" max="8000" step="50"
                  className="form-control form-control-sm text-center"
                  style={{ width: 70, fontSize: 12 }}
                  defaultValue={map.width}
                  key={`cw-${map.width}`}
                  onBlur={async e => {
                    const w = Math.max(400, Math.min(8000, parseInt(e.target.value) || map.width));
                    if (w === map.width) return;
                    try { await svc.updateMap(mapId, { width: w }); setMap(m => ({ ...m, width: w })); }
                    catch { toast.error('Failed to update canvas size'); }
                  }}
                  onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                />
                <span className="text-muted" style={{ fontSize: 12 }}>×</span>
                <input type="number" min="300" max="8000" step="50"
                  className="form-control form-control-sm text-center"
                  style={{ width: 70, fontSize: 12 }}
                  defaultValue={map.height}
                  key={`ch-${map.height}`}
                  onBlur={async e => {
                    const h = Math.max(300, Math.min(8000, parseInt(e.target.value) || map.height));
                    if (h === map.height) return;
                    try { await svc.updateMap(mapId, { height: h }); setMap(m => ({ ...m, height: h })); }
                    catch { toast.error('Failed to update canvas size'); }
                  }}
                  onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                />
                <span className="text-muted" style={{ fontSize: 12 }}>px</span>
              </div>
            </PF>
            <PF label="Sections">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted" style={{ fontSize: 12 }}>{sections.length}</span>
                {sections.length > 0 && (
                  <button className="btn btn-xs btn-outline-primary py-0 px-1" style={{ fontSize: 10 }}
                    onClick={selectAllSections}>
                    Select all
                  </button>
                )}
              </div>
            </PF>
            <PF label="Total seats">
              <span className="text-muted" style={{ fontSize: 12 }}>
                {sections.reduce((n, s) => n + (s.seats || []).length, 0)}
              </span>
            </PF>
            <PF label="Background">
              {map.background_image_url ? (
                <div>
                  <div className="d-flex gap-1 mb-2">
                    <label className="btn btn-sm btn-outline-secondary flex-grow-1 mb-0 py-1" style={{ fontSize: 11 }}>
                      Replace
                      <input type="file" accept="image/*" hidden onChange={handleBgUpload} />
                    </label>
                    <button className="btn btn-sm btn-outline-danger flex-grow-1 py-1" style={{ fontSize: 11 }}
                      onClick={removeBackground}>
                      Remove
                    </button>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span style={{ fontSize: 11, whiteSpace: 'nowrap', color: '#374151' }}>
                      Opacity {Math.round((map.bg_opacity ?? 0.5) * 100)}%
                    </span>
                    <input type="range" min={0.05} max={1} step={0.05}
                      value={map.bg_opacity ?? 0.5}
                      className="form-range flex-grow-1"
                      style={{ height: 4 }}
                      onChange={e => setMap(m => ({ ...m, bg_opacity: parseFloat(e.target.value) }))}
                      onMouseUp={e => svc.updateMap(mapId, { bg_opacity: parseFloat(e.target.value) })
                        .catch(() => {})}
                    />
                  </div>
                </div>
              ) : (
                <label className="btn btn-sm btn-outline-secondary w-100 mb-0 py-1" style={{ fontSize: 11 }}>
                  <i className="fas fa-image me-1" />Upload image
                  <input type="file" accept="image/*" hidden onChange={handleBgUpload} />
                </label>
              )}
            </PF>
          </>
        )}
        <hr className="my-3" />
        <p className="fw-semibold text-uppercase mb-2" style={{ fontSize: 10, color: '#6B7280', letterSpacing: 1 }}>
          Venue Objects
        </p>
        <div className="d-flex flex-wrap gap-1">
          {[{id:'stage',l:'Stage'},{id:'entrance',l:'Entry'},{id:'exit',l:'Exit'},{id:'bar',l:'Bar'},{id:'vip',l:'VIP'},{id:'text',l:'Text'}].map(o => (
            <button key={o.id} className="btn btn-outline-secondary"
              style={{ fontSize: 11, padding: '3px 9px' }}
              onClick={() => addObject(o.id)}>+ {o.l}</button>
          ))}
        </div>
        <hr className="my-3" />
        <p className="text-muted" style={{ fontSize: 11 }}>
          <kbd>V</kbd> Select &nbsp; <kbd>R</kbd> Rect &nbsp; <kbd>E</kbd> Ellipse &nbsp; <kbd>P</kbd> Polygon<br />
          <kbd>Ctrl+Z</kbd> Undo &nbsp; <kbd>Ctrl+D</kbd> Duplicate<br />
          <kbd>Ctrl+L</kbd> Lock/Unlock &nbsp; <kbd>Del</kbd> Delete<br />
          <kbd>←→↑↓</kbd> Nudge (+Shift×10)
        </p>
      </div>
    );
  }

  function SectionPanel() {
    const s    = selSection;
    const isObj = s?.shape_data?.isObject;
    return (
      <div className="p-3" style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 120px)' }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="fw-semibold mb-0" style={{ fontSize: 13 }}>{isObj ? 'Object' : 'Section'}</h6>
          <div className="d-flex gap-1">
            <button className={`btn btn-sm p-1 ${s.is_locked ? 'btn-warning' : 'btn-outline-secondary'}`}
              title={s.is_locked ? 'Unlock (Ctrl+L)' : 'Lock (Ctrl+L)'}
              onClick={() => patchSec(s.id, { is_locked: !s.is_locked }, true)}>
              <i className={`fas ${s.is_locked ? 'fa-lock' : 'fa-unlock'}`} style={{ fontSize: 10 }} />
            </button>
            {!isObj && (
              <button className="btn btn-sm btn-outline-secondary p-1" title="Duplicate (Ctrl+D)"
                onClick={() => duplicateSection(s.id)}>
                <i className="fas fa-copy" style={{ fontSize: 10 }} />
              </button>
            )}
            <button className="btn btn-sm btn-outline-danger p-1" title="Delete"
              onClick={() => removeSection(s.id)}>
              <i className="fas fa-trash-alt" style={{ fontSize: 10 }} />
            </button>
          </div>
        </div>

        <PF label="Name">
          <input className="form-control form-control-sm" value={s.name}
            onChange={e => patchSec(s.id, { name: e.target.value })}
            onBlur={commitPresent} />
        </PF>

        <PF label="Color">
          <div className="d-flex flex-wrap gap-1">
            {PALETTE.map(c => (
              <button key={c} type="button" onClick={() => patchSec(s.id, { color: c }, true)}
                style={{ width: 22, height: 22, borderRadius: '50%', background: c, border: 'none', cursor: 'pointer',
                  outline: s.color === c ? '2.5px solid #111' : '2px solid transparent', outlineOffset: 2 }} />
            ))}
          </div>
        </PF>

        {!isObj && (
          <>
            <PF label="Type">
              <div className="btn-group btn-group-sm w-100">
                <button type="button" className={`btn ${!s.has_seats ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => patchSec(s.id, { has_seats: false }, true)}>GA</button>
                <button type="button" className={`btn ${s.has_seats ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => patchSec(s.id, { has_seats: true }, true)}>Reserved</button>
              </div>
            </PF>

            <PF label="Default Price">
              <div className="input-group input-group-sm">
                <span className="input-group-text">QAR</span>
                <input type="number" min="0" step="0.01" className="form-control"
                  placeholder="0.00" value={s.default_price ?? ''}
                  onChange={e => patchSec(s.id, { default_price: e.target.value })}
                  onBlur={commitPresent} />
              </div>
            </PF>

            <PF label="Ticket Category">
              <select className="form-select form-select-sm"
                value={s.ticket_category_id || ''}
                onChange={e => patchSec(s.id, { ticket_category_id: e.target.value || null }, true)}>
                <option value="">— None —</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.category_name}</option>)}
              </select>
            </PF>

            {!s.has_seats && (
              <PF label="Capacity">
                <input type="number" min="1" className="form-control form-control-sm"
                  placeholder="Unlimited" value={s.capacity ?? ''}
                  onChange={e => patchSec(s.id, { capacity: e.target.value || null })}
                  onBlur={commitPresent} />
              </PF>
            )}
          </>
        )}

        <hr className="my-2" />
        <p className="fw-semibold text-uppercase mb-2" style={{ fontSize: 10, color: '#6B7280', letterSpacing: 1 }}>Transform</p>

        <PF label="Position">
          <div className="row g-1">
            <div className="col-6">
              <div className="input-group input-group-sm">
                <span className="input-group-text" style={{ fontSize: 10 }}>X</span>
                <input type="number" className="form-control" value={Math.round(s.position_x)}
                  onChange={e => moveSec(s.id, parseFloat(e.target.value) || 0, s.position_y)}
                  onBlur={commitPresent} />
              </div>
            </div>
            <div className="col-6">
              <div className="input-group input-group-sm">
                <span className="input-group-text" style={{ fontSize: 10 }}>Y</span>
                <input type="number" className="form-control" value={Math.round(s.position_y)}
                  onChange={e => moveSec(s.id, s.position_x, parseFloat(e.target.value) || 0)}
                  onBlur={commitPresent} />
              </div>
            </div>
          </div>
        </PF>

        <PF label="Size">
          <div className="row g-1">
            <div className="col-6">
              <div className="input-group input-group-sm">
                <span className="input-group-text" style={{ fontSize: 10 }}>W</span>
                <input type="number" min="40" className="form-control" value={Math.round(s.width)}
                  onChange={e => patchSec(s.id, { width: parseFloat(e.target.value) || 80 })}
                  onBlur={commitPresent} />
              </div>
            </div>
            <div className="col-6">
              <div className="input-group input-group-sm">
                <span className="input-group-text" style={{ fontSize: 10 }}>H</span>
                <input type="number" min="40" className="form-control" value={Math.round(s.height)}
                  onChange={e => patchSec(s.id, { height: parseFloat(e.target.value) || 60 })}
                  onBlur={commitPresent} />
              </div>
            </div>
          </div>
        </PF>

        <PF label={`Rotation: ${s.rotation || 0}°`}>
          <input type="range" className="form-range" min={-180} max={180} step={1}
            value={s.rotation || 0}
            onChange={e => patchSec(s.id, { rotation: parseInt(e.target.value) })}
            onMouseUp={commitPresent} />
        </PF>

        {!isObj && s.has_seats && (
          <>
            <hr className="my-2" />
            <p className="fw-semibold text-uppercase mb-2" style={{ fontSize: 10, color: '#6B7280', letterSpacing: 1 }}>
              Section Configuration
            </p>

            {/* Mode toggle */}
            <div className="btn-group btn-group-sm w-100 mb-3">
              <button type="button"
                className={`btn ${genMode === 'auto' ? 'btn-primary' : 'btn-outline-secondary'}`}
                style={{ fontSize: 10 }}
                onClick={() => setGenMode('auto')}>
                Auto-fill
              </button>
              <button type="button"
                className={`btn ${genMode === 'rows' ? 'btn-primary' : 'btn-outline-secondary'}`}
                style={{ fontSize: 10 }}
                onClick={() => setGenMode('rows')}>
                Row Config
              </button>
            </div>

            {genMode === 'auto' ? (
              <>
                <PF label="Total Seats">
                  <input type="number" min="1" max="50000" className="form-control form-control-sm" value={gen.count}
                    onChange={e => setGen(p => ({ ...p, count: e.target.value }))} />
                </PF>
                <PF label="Start #">
                  <input type="number" min="1" className="form-control form-control-sm" value={gen.startSeat}
                    onChange={e => setGen(p => ({ ...p, startSeat: e.target.value }))} />
                </PF>
              </>
            ) : (
              <>
                {/* Total rows */}
                <div className="mb-2">
                  <label className="form-label mb-1" style={{ fontSize: 11, color: '#374151', fontWeight: 600 }}>
                    Total rows
                  </label>
                  <div className="d-flex align-items-center gap-2">
                    <input type="number" min="1" max="999" className="form-control form-control-sm"
                      style={{ width: 70 }}
                      value={rowConfig.totalRows}
                      onChange={e => setRowConfig(c => ({ ...c, totalRows: e.target.value }))} />
                    <span style={{ fontSize: 11, color: '#6B7280' }}>rows in section</span>
                  </div>
                </div>

                {/* Default seats per row */}
                <div className="mb-2">
                  <label className="form-label mb-1" style={{ fontSize: 11, color: '#374151', fontWeight: 600 }}>
                    Default seats / row
                  </label>
                  <div className="d-flex align-items-center gap-2">
                    <input type="number" min="0" max="9999" className="form-control form-control-sm"
                      style={{ width: 70 }}
                      value={rowConfig.defaultSeats}
                      onChange={e => setRowConfig(c => ({ ...c, defaultSeats: e.target.value }))} />
                    <span style={{ fontSize: 11, color: '#6B7280' }}>applies to all rows unless overridden</span>
                  </div>
                </div>

                <hr className="my-2" />

                {/* Row overrides */}
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>Row overrides</span>
                  <span style={{ fontSize: 10, color: '#6B7280' }}>specific rows with different seat counts</span>
                </div>

                {rowConfig.overrides.length > 0 && (
                  <div className="mb-1" style={{ fontSize: 10, color: '#6B7280', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '2px 4px', alignItems: 'center' }}>
                    <span>From row</span><span>To row</span><span>Seats</span><span />
                  </div>
                )}

                {rowConfig.overrides.map(ov => (
                  <div key={ov.id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '2px 4px', alignItems: 'center', marginBottom: 4 }}>
                    <input type="number" min="1" className="form-control form-control-sm p-1"
                      style={{ fontSize: 11 }}
                      value={ov.fromRow}
                      onChange={e => setRowConfig(c => ({ ...c, overrides: c.overrides.map(o => o.id === ov.id ? { ...o, fromRow: e.target.value } : o) }))} />
                    <input type="number" min="1" className="form-control form-control-sm p-1"
                      style={{ fontSize: 11 }}
                      value={ov.toRow}
                      onChange={e => setRowConfig(c => ({ ...c, overrides: c.overrides.map(o => o.id === ov.id ? { ...o, toRow: e.target.value } : o) }))} />
                    <input type="number" min="0" className="form-control form-control-sm p-1"
                      style={{ fontSize: 11 }}
                      value={ov.seats}
                      onChange={e => setRowConfig(c => ({ ...c, overrides: c.overrides.map(o => o.id === ov.id ? { ...o, seats: e.target.value } : o) }))} />
                    <button className="btn btn-sm btn-outline-danger p-0"
                      style={{ width: 22, height: 22, fontSize: 12, lineHeight: 1 }}
                      onClick={() => setRowConfig(c => ({ ...c, overrides: c.overrides.filter(o => o.id !== ov.id) }))}>
                      ×
                    </button>
                  </div>
                ))}

                <button className="btn btn-sm btn-outline-secondary w-100 mb-2" style={{ fontSize: 11 }}
                  onClick={() => setRowConfig(c => ({ ...c, overrides: [...c.overrides, { id: tmpId(), fromRow: '1', toRow: '1', seats: '50' }] }))}>
                  + Add row override
                </button>

                {/* Preview */}
                {(() => {
                  const totalRows    = Math.max(1, parseInt(rowConfig.totalRows) || 0);
                  const defaultSeats = Math.max(0, parseInt(rowConfig.defaultSeats) || 0);
                  let total = 0;
                  for (let r = 1; r <= totalRows; r++) {
                    const ov = rowConfig.overrides.find(o => r >= parseInt(o.fromRow || 1) && r <= parseInt(o.toRow || 1));
                    total += ov ? Math.max(0, parseInt(ov.seats) || 0) : defaultSeats;
                  }
                  return (
                    <p style={{ fontSize: 11, color: '#6B7280', margin: '0 0 8px' }}>
                      Preview: <strong style={{ color: '#374151' }}>{total.toLocaleString()} seats</strong> across {totalRows} rows
                    </p>
                  );
                })()}
              </>
            )}

            {/* Row direction (shared between modes) */}
            <PF label="Row Direction">
              <div className="d-flex gap-1 align-items-center flex-wrap">
                <div className="btn-group btn-group-sm flex-shrink-0">
                  <button type="button"
                    className={`btn ${rowDirMode === 'auto' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    style={{ fontSize: 10 }}
                    onClick={() => setRowDirMode('auto')}>
                    Auto
                  </button>
                  <button type="button"
                    className={`btn ${rowDirMode === 'manual' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    style={{ fontSize: 10 }}
                    onClick={() => setRowDirMode('manual')}>
                    Manual
                  </button>
                </div>
                {rowDirMode === 'manual' ? (
                  <div className="input-group input-group-sm" style={{ width: 90 }}>
                    <input type="number" step="1" className="form-control form-control-sm p-1"
                      style={{ fontSize: 11 }}
                      value={rowDirAngle}
                      onChange={e => setRowDirAngle(e.target.value)} />
                    <span className="input-group-text p-1" style={{ fontSize: 11 }}>°</span>
                  </div>
                ) : selSection ? (() => {
                  const autoAngle = computeRowAngle(getSectionBasePoly(selSection));
                  return (
                    <span style={{ fontSize: 10, color: '#6B7280' }}>
                      ≈ {Math.round(autoAngle * 180 / Math.PI)}° from geometry
                    </span>
                  );
                })() : null}
              </div>
            </PF>

            {/* Spacing (shared between modes) */}
            <PF label="Spacing">
              <div className="btn-group btn-group-sm w-100">
                {['compact','normal','spacious'].map(d => (
                  <button key={d} type="button"
                    className={`btn ${gen.density === d ? 'btn-primary' : 'btn-outline-secondary'}`}
                    style={{ fontSize: 10, textTransform: 'capitalize' }}
                    onClick={() => setGen(p => ({ ...p, density: d }))}>
                    {d}
                  </button>
                ))}
              </div>
            </PF>

            {isTmp(s.id) ? (
              <p className="text-warning mb-2" style={{ fontSize: 11 }}>⚠ Save layout first</p>
            ) : (
              <button className="btn btn-sm btn-success w-100 mb-2" disabled={genBusy} onClick={handleGenerateSeats}>
                {genBusy ? 'Generating…' : `⚡ Generate Seats`}
              </button>
            )}
            {(s.seats || []).length > 0 && (
              <p className="text-center text-muted mb-0" style={{ fontSize: 11 }}>{s.seats.length} seats</p>
            )}
            {(s.seats || []).length > 0 && (
              <button className="btn btn-sm btn-outline-primary w-100 mt-2"
                onClick={() => enterDetailView(s)}>
                ✏ Edit Individual Seats
              </button>
            )}
          </>
        )}
      </div>
    );
  }

  function SeatPanel() {
    const singleId = selSeatIds.size === 1 ? [...selSeatIds][0] : null;
    const single   = singleId ? (selSection?.seats || []).find(s => s.id === singleId) : null;

    return (
      <div className="p-3" style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 120px)' }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="fw-semibold mb-0" style={{ fontSize: 13 }}>
            {selSeatIds.size > 1 ? `${selSeatIds.size} Seats` : 'Seat'}
          </h6>
          <button className="btn btn-sm btn-outline-danger p-1" onClick={deleteSelectedSeats}>
            <i className="fas fa-trash-alt" style={{ fontSize: 10 }} />
          </button>
        </div>

        {single && (
          <>
            <PF label="Label">
              <input className="form-control form-control-sm" value={single.label || ''}
                onChange={e => patchSeat(selSection.id, single.id, { label: e.target.value })}
                onBlur={commitPresent} />
            </PF>
            <div className="row g-1 mb-2">
              <div className="col-5">
                <PF label="Row">
                  <input className="form-control form-control-sm" value={single.row || ''}
                    onChange={e => patchSeat(selSection.id, single.id, { row: e.target.value })}
                    onBlur={commitPresent} />
                </PF>
              </div>
              <div className="col-7">
                <PF label="Seat #">
                  <input className="form-control form-control-sm" value={single.seat_number || ''}
                    onChange={e => patchSeat(selSection.id, single.id, { seat_number: e.target.value })}
                    onBlur={commitPresent} />
                </PF>
              </div>
            </div>
            <PF label="Price override">
              <div className="input-group input-group-sm">
                <span className="input-group-text">QAR</span>
                <input type="number" min="0" step="0.01" className="form-control"
                  placeholder={selSection?.default_price ?? 'Section price'}
                  value={single.price_override ?? ''}
                  onChange={e => patchSeat(selSection.id, single.id, { price_override: e.target.value === '' ? null : parseFloat(e.target.value) })}
                  onBlur={commitPresent} />
              </div>
              {single.price_override != null && (
                <button className="btn btn-link btn-sm p-0 mt-1" style={{ fontSize: 11 }}
                  onClick={() => patchSeat(selSection.id, single.id, { price_override: null }, true)}>
                  Reset to section price
                </button>
              )}
            </PF>
            <PF label="Status">
              <select className="form-select form-select-sm" value={single.status}
                onChange={e => patchSeat(selSection.id, single.id, { status: e.target.value }, true)}>
                <option value="available">Available</option>
                <option value="blocked">Blocked</option>
                <option value="disabled">Disabled</option>
              </select>
            </PF>
            <div className="form-check mb-2">
              <input type="checkbox" className="form-check-input" id="handicapped-chk"
                checked={!!single.is_handicapped}
                onChange={e => patchSeat(selSection.id, single.id, { is_handicapped: e.target.checked }, true)} />
              <label className="form-check-label" htmlFor="handicapped-chk" style={{ fontSize: 12 }}>
                ♿ Handicapped seat
              </label>
            </div>
            <p className="text-muted mb-2" style={{ fontSize: 11 }}>
              ({Math.round(single.x)}, {Math.round(single.y)}) · drag to reposition
            </p>
          </>
        )}

        <hr className="my-2" />
        <p className="fw-semibold text-uppercase mb-2" style={{ fontSize: 10, color: '#6B7280', letterSpacing: 1 }}>Bulk</p>

        <PF label="Set price (all selected)">
          <div className="input-group input-group-sm">
            <span className="input-group-text">QAR</span>
            <input type="number" min="0" step="0.01" className="form-control"
              placeholder="Price" value={bulkPrice}
              onChange={e => setBulkPrice(e.target.value)} />
            <button className="btn btn-outline-primary" onClick={() => {
              patchSelectedSeats({ price_override: bulkPrice === '' ? null : parseFloat(bulkPrice) });
              setBulkPrice('');
            }}>Set</button>
          </div>
        </PF>

        <div className="d-flex gap-1 mb-2">
          <button className="btn btn-xs btn-outline-secondary flex-grow-1" style={{ fontSize: 11 }}
            onClick={() => patchSelectedSeats({ price_override: null })}>
            Reset prices
          </button>
          <button className="btn btn-xs btn-outline-secondary flex-grow-1" style={{ fontSize: 11 }}
            onClick={() => patchSelectedSeats({ status: 'blocked' })}>
            Block
          </button>
        </div>


        <hr className="my-2" />
        <p className="fw-semibold text-uppercase mb-2" style={{ fontSize: 10, color: '#6B7280', letterSpacing: 1 }}>Number</p>
        <div className="row g-1 mb-2">
          <div className="col-6">
            <button className="btn btn-xs btn-outline-secondary w-100" style={{ fontSize: 11 }}
              onClick={() => numberSelectedSeats(1, false)}>→ 1,2,3…</button>
          </div>
          <div className="col-6">
            <button className="btn btn-xs btn-outline-secondary w-100" style={{ fontSize: 11 }}
              onClick={() => numberSelectedSeats(1, true)}>← 1,2,3…</button>
          </div>
        </div>

        <hr className="my-2" />
        <p className="fw-semibold text-uppercase mb-2" style={{ fontSize: 10, color: '#6B7280', letterSpacing: 1 }}>Relabel</p>
        {(() => {
          const selList = selSection ? (selSection.seats || []).filter(s => selSeatIds.has(s.id)) : [];
          const xs = selList.map(s => s.x);
          const ys = selList.map(s => s.y);
          const xSpan = xs.length > 1 ? Math.max(...xs) - Math.min(...xs) : 0;
          const ySpan = ys.length > 1 ? Math.max(...ys) - Math.min(...ys) : 0;
          const isH = ySpan < xSpan * 0.25 && xSpan > 10;
          const isV = xSpan < ySpan * 0.25 && ySpan > 10;
          const dirs = isH
            ? [{ id: 'ltr', label: '→ L→R' }, { id: 'rtl', label: '← R→L' }]
            : isV
              ? [{ id: 'ttb', label: '↓ T→B' }, { id: 'btt', label: '↑ B→T' }]
              : [
                  { id: 'ltr', label: '→ L→R' }, { id: 'rtl', label: '← R→L' },
                  { id: 'ttb', label: '↓ T→B' }, { id: 'btt', label: '↑ B→T' },
                ];
          const currentDir = dirs.find(d => d.id === relabelDir) ? relabelDir : dirs[0].id;
          return (
            <>
              <div className="mb-1" style={{ fontSize: 11, color: '#6B7280' }}>
                Start label <span style={{ fontStyle: 'italic' }}>(e.g. B3)</span>
              </div>
              <div className="d-flex gap-1 mb-1">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="B3"
                  value={relabelStart}
                  onChange={e => setRelabelStart(e.target.value)}
                  style={{ fontSize: 12, width: 72, flexShrink: 0 }}
                />
                <select
                  className="form-select form-select-sm"
                  value={currentDir}
                  onChange={e => setRelabelDir(e.target.value)}
                  style={{ fontSize: 11 }}
                >
                  {dirs.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
                </select>
              </div>
              <button
                className="btn btn-xs btn-outline-primary w-100 mb-1"
                style={{ fontSize: 11 }}
                disabled={selSeatIds.size === 0 || !relabelStart.trim()}
                onClick={() => relabelSelectedSeats(relabelStart, currentDir)}
              >
                Apply labels ({selSeatIds.size} seats)
              </button>
              {selList.length > 1 && (
                <p className="text-muted mb-0" style={{ fontSize: 10 }}>
                  {isH ? 'Horizontal selection detected' : isV ? 'Vertical selection detected' : 'Mixed selection — all directions available'}
                </p>
              )}
            </>
          );
        })()}

        <hr className="my-2" />
        <p className="fw-semibold text-uppercase mb-2" style={{ fontSize: 10, color: '#6B7280', letterSpacing: 1 }}>Align</p>
        <div className="d-flex gap-1 flex-wrap mb-1">
          {[
            { fn: () => alignSeats('x','min'), t: 'Align Left',    ic: '⊢' },
            { fn: () => alignSeats('x','mid'), t: 'Center H',      ic: '↔' },
            { fn: () => alignSeats('x','max'), t: 'Align Right',   ic: '⊣' },
            { fn: () => alignSeats('y','min'), t: 'Align Top',     ic: '⊤' },
            { fn: () => alignSeats('y','mid'), t: 'Center V',      ic: '↕' },
            { fn: () => alignSeats('y','max'), t: 'Align Bottom',  ic: '⊥' },
          ].map((a, i) => (
            <button key={i} title={a.t} className="btn btn-xs btn-outline-secondary"
              style={{ width: 30, height: 26, fontSize: 13, padding: 0 }}
              disabled={selSeatIds.size < 2} onClick={a.fn}>{a.ic}</button>
          ))}
        </div>
        <div className="d-flex gap-1">
          <button className="btn btn-xs btn-outline-secondary flex-grow-1" style={{ fontSize: 11 }}
            disabled={selSeatIds.size < 2} onClick={() => distributeSeats('x')}>Distribute H</button>
          <button className="btn btn-xs btn-outline-secondary flex-grow-1" style={{ fontSize: 11 }}
            disabled={selSeatIds.size < 2} onClick={() => distributeSeats('y')}>Distribute V</button>
        </div>

        <hr className="my-2" />
        <button className="btn btn-sm btn-outline-secondary w-100"
          onClick={() => { setSelSeatIds(new Set()); }}>
          ← Deselect seats
        </button>
      </div>
    );
  }

  // ══ SCREENS ════════════════════════════════════════════════════════════════════

  if (loading) return <div className="text-center mt-5 text-muted">Loading…</div>;

  if (!map) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '70vh', padding: 24 }}>
        <div style={{ width: '100%', maxWidth: 680 }}>
          <h4 className="mb-1 text-gray-800">Set Up Seat Map</h4>
          <p className="text-muted mb-4" style={{ fontSize: 14 }}>
            Start from scratch or apply a pre-built template to get started quickly.
          </p>

          <div className="row g-3">
            {/* Start from scratch */}
            <div className="col-md-6">
              <div className="card shadow-sm h-100">
                <div className="card-body p-4">
                  <div className="mb-3">
                    <i className="fas fa-drafting-compass fa-2x" style={{ color: '#4e73df' }}></i>
                  </div>
                  <h6 className="font-weight-bold mb-1">Start from Scratch</h6>
                  <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                    Draw and customise your own layout section by section.
                  </p>
                  <form onSubmit={handleCreateMap}>
                    <input className="form-control form-control-sm mb-2" placeholder="Map name (e.g. Main Hall)"
                      value={newMapName} required onChange={e => setNewMapName(e.target.value)} />
                    <button type="submit" className="btn btn-primary btn-sm w-100">Create Blank Map</button>
                  </form>
                </div>
              </div>
            </div>

            {/* Use a template */}
            <div className="col-md-6">
              <div className="card shadow-sm h-100">
                <div className="card-body p-4">
                  <div className="mb-3">
                    <i className="fas fa-map fa-2x" style={{ color: '#1cc88a' }}></i>
                  </div>
                  <h6 className="font-weight-bold mb-1">Use a Template</h6>
                  <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                    Pick a pre-built layout created by the admin and customise it for your event.
                  </p>
                  {templates === null ? (
                    <button className="btn btn-success btn-sm w-100" onClick={loadTemplates} disabled={loadingTpl}>
                      {loadingTpl ? 'Loading…' : 'Browse Templates'}
                    </button>
                  ) : templates.length === 0 ? (
                    <p className="text-muted" style={{ fontSize: 13 }}>No templates available yet.</p>
                  ) : (
                    <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                      {templates.map(tpl => (
                        <button
                          key={tpl.id}
                          className="btn btn-outline-secondary btn-sm w-100 text-left mb-1 d-flex align-items-center justify-content-between"
                          style={{ fontSize: 13 }}
                          onClick={() => handleApplyTemplate(tpl.id)}
                          disabled={applyingTpl}
                        >
                          <span>
                            <i className="fas fa-map-marked-alt mr-2 text-muted"></i>
                            {tpl.name}
                          </span>
                          <span className="text-muted" style={{ fontSize: 11 }}>
                            {tpl.sections_count ?? 0} sections
                          </span>
                        </button>
                      ))}
                      {applyingTpl && <p className="text-center text-muted mt-2" style={{ fontSize: 12 }}>Applying…</p>}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const svgCursor = spaceCursor ? 'grab' : tool === 'select' ? 'default' : 'crosshair';
  const isDirty   = past.length > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>

      {/* ── Top bar ─────────────────────────────────────────────────────────── */}
      <div className="d-flex align-items-center gap-2 px-3 py-2 bg-white border-bottom"
        style={{ flexShrink: 0, zIndex: 10 }}>

        <span className="fw-bold" style={{ fontSize: 14 }}>{map.name}</span>
        <span className={`badge ${map.status === 'published' ? 'bg-success' : 'bg-secondary'}`}
          style={{ fontSize: 10 }}>
          {map.status === 'published' ? 'Published' : 'Draft'}
        </span>
        {isDirty && <span style={{ fontSize: 10, color: '#F59E0B' }}>● unsaved</span>}

        <div className="btn-group btn-group-sm ms-2">
          <button className="btn btn-outline-secondary" onClick={() => setZoom(z => clamp(z + 0.15, 0.12, 8))} title="Zoom in">+</button>
          <button className="btn btn-outline-secondary"
            onClick={() => { setZoom(1); setPan({ x: 60, y: 60 }); }} title="Reset view">⟳</button>
          <button className="btn btn-outline-secondary" onClick={() => setZoom(z => clamp(z - 0.15, 0.12, 8))} title="Zoom out">−</button>
        </div>
        <span className="text-muted" style={{ fontSize: 11 }}>{Math.round(zoom * 100)}%</span>

        <button className={`btn btn-sm ${showGrid   ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => setShowGrid(v => !v)} title="Grid">
          <i className="fas fa-th" style={{ fontSize: 11 }} />
        </button>
        <button className={`btn btn-sm ${snapGrid   ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => setSnapGrid(v => !v)} title="Snap">
          <i className="fas fa-magnet" style={{ fontSize: 11 }} />
        </button>
        <button className={`btn btn-sm ${showLabels ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => setShowLabels(v => !v)} title="Seat labels">
          <i className="fas fa-tag" style={{ fontSize: 11 }} />
        </button>

        <div className="btn-group btn-group-sm">
          <button className="btn btn-outline-secondary" disabled={!past.length}
            onClick={undo} title="Undo Ctrl+Z">
            <i className="fas fa-undo" style={{ fontSize: 11 }} />
          </button>
          <button className="btn btn-outline-secondary" disabled={!future.length}
            onClick={redo} title="Redo Ctrl+Shift+Z">
            <i className="fas fa-redo" style={{ fontSize: 11 }} />
          </button>
        </div>

        <div className="ms-auto d-flex gap-2">
          <button className="btn btn-sm btn-outline-secondary"
            onClick={() => navigate('/organizer/events')}>← Events</button>
          <button className="btn btn-sm btn-outline-secondary"
            title="Import shapes from an SVG file as sections"
            onClick={() => { setSvgParsed(null); setSvgClearExisting(false); setSvgModal(true); }}>
            <i className="fas fa-file-import" style={{ fontSize: 11, marginRight: 4 }} />
            Import SVG
          </button>
          <button className="btn btn-sm btn-outline-primary" onClick={handlePublish}>
            {map.status === 'published' ? 'Unpublish' : '🚀 Publish'}
          </button>
          <button className="btn btn-sm btn-success" disabled={saving} onClick={handleSave}>
            {saving ? 'Saving…' : '💾 Save'}
          </button>
        </div>
      </div>

      {/* ── Contextual hints ─────────────────────────────────────────────────── */}
      {tool === 'polygon' && (
        <div className="text-center py-1 border-bottom"
          style={{ flexShrink: 0, background: '#EFF6FF', fontSize: 12, color: '#1D4ED8' }}>
          Click to add points &nbsp;·&nbsp; <strong>Click first point</strong> or <strong>double-click</strong> to close &nbsp;·&nbsp; <strong>Esc</strong> to cancel
          {polyPts.length > 0 && <span className="ms-2 badge" style={{ background: '#1D4ED8' }}>{polyPts.length} pts</span>}
        </div>
      )}
      {viewMode === 'detail' && detailSection && (
        <div className="d-flex align-items-center gap-3 px-3 py-1 border-bottom"
          style={{ flexShrink: 0, background: '#F0FDF4', fontSize: 12, color: '#166534' }}>
          <span>
            <strong>Section Detail:</strong> {detailSection.name}
            &nbsp;·&nbsp; Click to select seats &nbsp;·&nbsp; Ctrl+click multi-select &nbsp;·&nbsp; Drag rubber-band &nbsp;·&nbsp; <strong>Esc</strong> to return
          </span>
          <button className="btn btn-sm ms-auto"
            style={{ background: '#16a34a', color: 'white', fontSize: 11, padding: '2px 14px', borderRadius: 20, border: 'none' }}
            onClick={exitDetailView}>
            ← Back to Map
          </button>
        </div>
      )}
      {viewMode === 'map' && editSeats && selSection && (
        <div className="d-flex align-items-center justify-content-between px-3 py-1 border-bottom"
          style={{ flexShrink: 0, background: '#FFF7ED', fontSize: 12, color: '#92400E' }}>
          <span>
            <strong>Seat Edit</strong> — {selSection.name} &nbsp;·&nbsp;
            Click to select &nbsp;· Ctrl+click multi-select · Drag rubber-band · "Add Seat" tool to place new seat
          </span>
          <button className="btn btn-xs btn-outline-warning" style={{ fontSize: 11 }}
            onClick={() => { setEditSeats(false); setSelSeatIds(new Set()); }}>
            ✕ Done
          </button>
        </div>
      )}

      {/* ── Body ─────────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Left toolbar */}
        <div className="border-end d-flex flex-column align-items-center py-2 gap-1"
          style={{ width: 48, flexShrink: 0, background: '#F8FAFC' }}>
          {[
            { id: 'select',  icon: 'fa-mouse-pointer', tip: 'Select/Move (V)' },
            { id: 'rect',    icon: 'fa-vector-square',  tip: 'Rectangle (R)' },
            { id: 'circle',  icon: 'fa-circle',         tip: 'Ellipse (E)' },
            { id: 'polygon', icon: 'fa-draw-polygon',   tip: 'Polygon (P)' },
            { id: 'addseat', icon: 'fa-plus-circle',    tip: 'Add Seat (A)' },
          ].map(t => (
            <button key={t.id} title={t.tip}
              className={`btn btn-sm ${tool === t.id ? 'btn-primary' : 'btn-outline-secondary'}`}
              style={{ width: 36, height: 36, padding: 0 }}
              onClick={() => { setTool(t.id); setPolyPts([]); setPolyMouse(null); }}>
              <i className={`fas ${t.icon}`} style={{ fontSize: 12 }} />
            </button>
          ))}

          <div style={{ width: '80%', borderTop: '1px solid #e2e8f0', margin: '4px 0' }} />

          <button className="btn btn-sm btn-outline-secondary" style={{ width: 36, height: 36, padding: 0 }}
            title="Undo (Ctrl+Z)" disabled={!past.length} onClick={undo}>
            <i className="fas fa-undo" style={{ fontSize: 12 }} />
          </button>
          <button className="btn btn-sm btn-outline-secondary" style={{ width: 36, height: 36, padding: 0 }}
            title="Redo" disabled={!future.length} onClick={redo}>
            <i className="fas fa-redo" style={{ fontSize: 12 }} />
          </button>

          <div style={{ width: '80%', borderTop: '1px solid #e2e8f0', margin: '4px 0' }} />

          <button className="btn btn-sm btn-outline-danger" style={{ width: 36, height: 36, padding: 0 }}
            title="Delete (Del)"
            onClick={() => { if (selSeatIds.size > 0) deleteSelectedSeats(); else if (selSecId) removeSection(selSecId); }}>
            <i className="fas fa-trash" style={{ fontSize: 12 }} />
          </button>
        </div>

        {/* Sections list */}
        <div className="border-end d-flex flex-column" style={{ width: 158, flexShrink: 0, overflowY: 'auto', background: '#FAFAFA' }}>
          {viewMode === 'detail' && (
            <div className="px-2 pt-2 pb-1" style={{ flexShrink: 0 }}>
              <button className="btn btn-sm w-100" style={{ background: '#16a34a', color: 'white', fontSize: 11, borderRadius: 14 }}
                onClick={exitDetailView}>← Back to Map</button>
            </div>
          )}
          <div className="p-2 flex-grow-1">
            <p className="text-muted fw-semibold text-uppercase mb-2" style={{ fontSize: 10, letterSpacing: 1 }}>
              Sections ({sections.length})
            </p>
            {sections.length === 0 && (
              <p className="text-muted text-center mt-3" style={{ fontSize: 12 }}>Draw a section<br />to start</p>
            )}
            {sections.map(sec => {
              const strId = String(sec.id);
              const isSel = selSecIds.has(strId);
              const isActive = viewMode === 'detail' && strId === String(detailSecId);
              return (
                <div key={sec.id}
                  className="rounded px-2 py-1 mb-1 d-flex align-items-center gap-2"
                  style={{ cursor: 'pointer', fontSize: 12,
                    border: `1px solid ${isActive ? sec.color : isSel ? sec.color : 'transparent'}`,
                    background: isActive ? `${sec.color}33` : isSel ? `${sec.color}22` : 'transparent' }}
                  onClick={() => {
                    if (viewMode === 'detail') {
                      if (sec.has_seats && !sec.shape_data?.isObject) enterDetailView(sec);
                    } else {
                      setSelSecId(sec.id); setSelSecIds(new Set([strId])); setSelSeatIds(new Set()); setEditSeats(false);
                    }
                  }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: sec.color, flexShrink: 0 }} />
                  <span className="text-truncate text-dark flex-grow-1">{sec.name}</span>
                  {sec.is_locked && <i className="fas fa-lock text-muted" style={{ fontSize: 9 }} />}
                  {sec.shape_data?.isObject && <i className="fas fa-cube text-muted" style={{ fontSize: 9 }} />}
                  {!sec.shape_data?.isObject && sec.has_seats && viewMode === 'map' && (
                    <span title="Edit Seats" style={{ fontSize: 10, color: sec.color, lineHeight: 1 }}
                      onClick={e => { e.stopPropagation(); enterDetailView(sec); }}>✏</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Canvas */}
        <div style={{ flex: 1, overflow: 'hidden', position: 'relative', background: '#F1F5F9' }}>
          <svg ref={svgRef} width="100%" height="100%"
            style={{ display: 'block', cursor: svgCursor }}
            onMouseDown={onBgMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={() => { const t = drag.current?.type; if (t !== 'seats' && t !== 'sec' && t !== 'resize' && t !== 'rotate' && t !== 'bg-move' && t !== 'bg-resize') { drag.current = null; setDrawPrev(null); } }}
            onClick={onCanvasClick}
            onDoubleClick={onCanvasDblClick}>

            <g style={{
              transform: `translate(${pan.x}px,${pan.y}px) scale(${zoom})`,
              transformOrigin: '0 0',
              transition: transitioning ? 'transform 0.4s cubic-bezier(0.25,0.46,0.45,0.94)' : 'none',
            }}>
              <rect x={0} y={0} width={map.width} height={map.height}
                fill="white" stroke="#CBD5E1" strokeWidth={1} rx={8} />
              {renderBgImage()}
              {renderGrid()}
              {sections.map(sec => renderSection(sec))}
              {renderDrawPrev()}
              {renderPolyPrev()}
              {/* Rubber band selection rect */}
              {rubber && (
                <rect x={Math.min(rubber.x0,rubber.x1)} y={Math.min(rubber.y0,rubber.y1)}
                  width={Math.abs(rubber.x1-rubber.x0)} height={Math.abs(rubber.y1-rubber.y0)}
                  fill="rgba(59,130,246,0.07)" stroke="#3B82F6" strokeWidth={1} strokeDasharray="4 2"
                  pointerEvents="none" />
              )}
            </g>
          </svg>
        </div>

        {/* Right panel */}
        <div className="border-start bg-white" style={{ width: 264, flexShrink: 0, overflowY: 'auto' }}>
          {renderPanel()}
        </div>
      </div>

      {/* ── SVG Import Modal ─────────────────────────────────────────────────── */}
      {svgModal && (
        <div
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => setSvgModal(false)}
        >
          <div
            style={{ background:'#fff', borderRadius:12, width:520, maxWidth:'95vw', boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ padding:'18px 24px 14px', borderBottom:'1px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <h5 style={{ margin:0, fontSize:16, fontWeight:700 }}>Import SVG</h5>
                <p style={{ margin:'2px 0 0', fontSize:12, color:'#6B7280' }}>
                  Shapes become sections — you can rename and configure each after importing.
                </p>
              </div>
              <button onClick={() => setSvgModal(false)} style={{ background:'none', border:'none', fontSize:20, color:'#9CA3AF', cursor:'pointer', lineHeight:1 }}>×</button>
            </div>

            {/* Body */}
            <div style={{ padding:'20px 24px' }}>
              {/* Drop zone */}
              <div
                onDragOver={e => { e.preventDefault(); setSvgDragOver(true); }}
                onDragLeave={() => setSvgDragOver(false)}
                onDrop={e => {
                  e.preventDefault(); setSvgDragOver(false);
                  handleSvgFile(e.dataTransfer.files[0]);
                }}
                onClick={() => svgFileRef.current?.click()}
                style={{
                  border:`2px dashed ${svgDragOver ? '#3B82F6' : '#D1D5DB'}`,
                  borderRadius:10,
                  background: svgDragOver ? '#EFF6FF' : '#F9FAFB',
                  padding:'32px 16px',
                  textAlign:'center',
                  cursor:'pointer',
                  transition:'all 0.15s',
                  marginBottom:16,
                }}
              >
                <input ref={svgFileRef} type="file" accept=".svg,image/svg+xml" style={{ display:'none' }}
                  onChange={e => handleSvgFile(e.target.files[0])} />
                <i className="fas fa-file-upload" style={{ fontSize:28, color: svgDragOver ? '#3B82F6' : '#9CA3AF', marginBottom:10, display:'block' }} />
                <p style={{ margin:0, fontSize:14, fontWeight:600, color: svgDragOver ? '#1D4ED8' : '#374151' }}>
                  {svgDragOver ? 'Drop the SVG here' : 'Drag & drop an SVG file'}
                </p>
                <p style={{ margin:'4px 0 0', fontSize:12, color:'#9CA3AF' }}>or click to browse</p>
              </div>

              {/* Parse result */}
              {svgParsed && (
                <>
                  <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:8, padding:'10px 14px', marginBottom:14 }}>
                    <p style={{ margin:0, fontSize:13, fontWeight:600, color:'#166534' }}>
                      <i className="fas fa-check-circle" style={{ marginRight:6 }} />
                      {svgParsed.length} section{svgParsed.length !== 1 ? 's' : ''} detected
                    </p>
                  </div>
                  <div style={{ maxHeight:160, overflowY:'auto', marginBottom:14, border:'1px solid #E5E7EB', borderRadius:8 }}>
                    {svgParsed.map((s, i) => (
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'7px 12px', borderBottom: i<svgParsed.length-1 ? '1px solid #F3F4F6' : 'none' }}>
                        <span style={{ width:14, height:14, borderRadius:3, background:s.color, flexShrink:0, border:'1px solid rgba(0,0,0,0.1)' }} />
                        <span style={{ fontSize:13, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.name}</span>
                        <span style={{ fontSize:11, color:'#9CA3AF', flexShrink:0 }}>
                          {s.shape_type} · {Math.round(s.width)}×{Math.round(s.height)}
                        </span>
                      </div>
                    ))}
                  </div>

                  <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#374151', cursor:'pointer', marginBottom:4 }}>
                    <input type="checkbox" checked={svgClearExisting} onChange={e => setSvgClearExisting(e.target.checked)} />
                    Clear existing sections before importing
                  </label>
                  {svgClearExisting && (
                    <p style={{ fontSize:11, color:'#DC2626', margin:'2px 0 0' }}>
                      ⚠ This will remove all {sections.length} existing section{sections.length !== 1 ? 's' : ''}.
                    </p>
                  )}
                </>
              )}

              {!svgParsed && (
                <p style={{ fontSize:12, color:'#9CA3AF', textAlign:'center', margin:0 }}>
                  Supports <strong>rect</strong>, <strong>circle</strong>, <strong>ellipse</strong>, <strong>polygon</strong>, and <strong>path</strong> elements.
                  Nested <code>&lt;g&gt;</code> groups with <code>id</code> attributes become section names.
                </p>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding:'14px 24px', borderTop:'1px solid #E5E7EB', display:'flex', justifyContent:'flex-end', gap:10 }}>
              <button onClick={() => setSvgModal(false)}
                style={{ padding:'7px 18px', borderRadius:8, border:'1px solid #D1D5DB', background:'#fff', cursor:'pointer', fontSize:13 }}>
                Cancel
              </button>
              <button onClick={doSvgImport} disabled={!svgParsed?.length}
                style={{ padding:'7px 18px', borderRadius:8, border:'none', background: svgParsed?.length ? '#3B82F6' : '#D1D5DB', color:'#fff', cursor: svgParsed?.length ? 'pointer' : 'default', fontSize:13, fontWeight:600 }}>
                Import {svgParsed ? `${svgParsed.length} section${svgParsed.length !== 1 ? 's' : ''}` : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PF({ label, children }) {
  return (
    <div className="mb-2">
      <label className="d-block text-muted mb-1" style={{ fontSize: 11 }}>{label}</label>
      {children}
    </div>
  );
}
